/**
 * The due diligence domain.
 *
 * A **diligence** is the operation. Inside it live the **subjects** analyzed —
 * a person, a company or a property — and every subject carries the
 * **certificates** its type requires.
 *
 * The three aggregates are here, along with the catalogue that says which
 * certificates each kind of subject demands. Nothing in this file imports a
 * library, reads a clock or knows a database exists.
 */
import { cleanIdentifiers, type PropertyIdentifiers } from './property-identifiers'
import { MANUAL_FINDING_KEY, MIN_EVIDENCE_LENGTH, type CertificateReading } from './certificate-reading'
import { defaultBasisFor, type LegalBasis, type LegalGround, type QueryGround, type QueryRequester } from './legal-basis'
import { changeBetween, type VerdictChange } from './monitoring'
import type { SubjectRole } from './catalog/blocks'
import type { Divergence } from './catalog/standing'
import type { Purpose } from './catalog/requirement'
import { checkSpend, MIN_APPROVAL_REASON, spentCentsOf, validBudget, type BudgetApproval } from './budget'
import {
  assigneeNotEligible,
  certificateNotManual,
  forbidden,
  invalidClientProfile,
  invalidHomeState,
  invalidInput,
  notFound,
  purgeOnHold,
  type AppError
} from './errors'
import type { Money } from './finance/money'
import { dueAtFor, RETENTION_POLICY } from './retention'
import { err, ok, type Result } from './result'
import { STATES } from './property-registration'
import type { SourceKey } from './sources'
import type { UserRole } from './user'

/** What the diligence looks at: a person, a company or a property. */
export type SubjectType = 'cpf' | 'cnpj' | 'property'

/** The states the database keeps — the screen derives nine out of them. */
export type CertificateStatus = 'pending' | 'running' | 'clear' | 'warning' | 'adverse' | 'failed' | 'purged'

export type CertificateCategory =
  | 'tax'
  | 'judicial'
  | 'labor'
  | 'criminal'
  | 'compliance'
  | 'registration'
  | 'propertyRegistry'
  | 'financial'
  | 'environmental'
  | 'technical'

export type DiligenceStatus = 'inProgress' | 'approved' | 'approvedWithNotes' | 'rejected' | 'purged'

/**
 * How the document actually arrived.
 *
 * Not the same question as `automated`, which says whether a robot *exists*
 * for this certificate. This says what happened to this one: the robot went,
 * a person went, the portal mailed it, or a sibling certificate answered for
 * it. The dossier looks identical either way — that is the point of the manual
 * queue — so the origin is the only place the difference survives, and it is
 * what an audit asks for first.
 */
export type CertificateOrigin = 'scraper' | 'provider' | 'manual' | 'email' | 'mirror' | 'counterparty'

export const ORIGIN_LABEL: Record<CertificateOrigin, string> = {
  scraper: 'Robô',
  provider: 'Fornecedor contratado',
  manual: 'Emissão manual',
  email: 'Recebida por e-mail',
  mirror: 'Copiada de certidão equivalente',
  counterparty: 'Enviado pela contraparte'
}

/** No document and no answer. */
export const NOT_OBTAINED_LABEL = 'Não obtida'

/** An answer from before `origin` was recorded, with nothing left to tell who brought it. */
export const UNRECORDED_ORIGIN_LABEL = 'Origem não registrada (anterior ao registro)'

/**
 * Documents somebody of the office went and got, and so looked at.
 *
 * Not the counterparty's uploads: the seller sending their own certificate is
 * the party with an interest in it, and nobody of the office checked whose
 * name is on the paper.
 */
export const OFFICE_BROUGHT_ORIGINS: readonly CertificateOrigin[] = ['manual']

/**
 * Why a search did not produce a document.
 *
 * The three are not degrees of the same thing — they are different situations
 * with different answers, and collapsing them is why the queue retries what
 * cannot succeed.
 *
 * - `technical`: the portal was down, the layout changed, the captcha won.
 *   Nothing is known about the subject; trying again is exactly right.
 * - `missingData`: the portal demands a field the case does not have. Retrying
 *   changes nothing until a person fills it in.
 * - `refused`: **the portal answered.** It declined to issue a negative
 *   certificate because there are debts or pendencies — which is a finding
 *   about the subject, not a failure of ours. Retrying burns another search to
 *   be told the same thing.
 */
export type FailureKind = 'technical' | 'missingData' | 'refused'

export const FAILURE_LABEL: Record<FailureKind, string> = {
  technical: 'Falha técnica na coleta',
  missingData: 'Falta dado que o portal exige',
  refused: 'Órgão não emitiu — há pendências'
}

/**
 * Phrases a portal uses when the problem is an account of ours, not the
 * search — what `needsExternalFix` looks for in the diagnostic.
 */
const NEEDS_EXTERNAL_FIX = ['saldo insuficiente', 'credenciais', 'verifique a conta', 'não configurada', 'contrato com pend', 'certificado a1']

import type { CompanyPartner } from './company-partner'

export type { CompanyPartner }

/**
 * What the public registry knows about a company.
 *
 * It is not a certificate and must not be read as one: the registry mirror is
 * fast and free, and a certidão da Junta is what proves anything. This is the
 * picture an analyst needs on the screen before deciding what to ask for.
 */
export type CompanyProfile = {
  tradeName: string | null
  /** The main activity, with the code the registry uses. */
  cnae: string | null
  cnaeDescription: string | null
  /** Declared capital, in cents — never a float of reais. */
  capitalCents: number | null
  /** When the company opened, `YYYY-MM-DD`. */
  openedOn: string | null
  /** "ATIVA", "BAIXADA", "SUSPENSA" — the first thing worth knowing. */
  registryStatus: string | null
  legalNature: string | null
  partners: CompanyPartner[]
  /**
   * Where the company is seated — the UF and city of its head office.
   *
   * Where its state and municipal clearances are issued, and the comarca its
   * own lawsuits are filed in. `null` when the registry did not say; absent on
   * a profile captured before this was kept, which is asked again.
   */
  seat?: { uf: string; city: string } | null
  /** When we asked, because the registry changes and the dossier is dated. */
  fetchedAt: Date
}

/** Extra personal fields some portals demand before issuing the certificate. */
export type PersonalField = 'nationalId' | 'birthDate' | 'motherName' | 'gender'

export const PERSONAL_FIELDS: readonly PersonalField[] = ['nationalId', 'birthDate', 'motherName', 'gender']

// ─── Certificate ────────────────────────────────────────────────────────────

export type CertificateProps = {
  id: string
  subjectId: string
  name: string
  category: CertificateCategory
  status: CertificateStatus
  automated: boolean
  /**
   * The template's price when the line was opened, in centavos — the estimate.
   *
   * Never what was spent: a line nobody asked cost nothing, and a query the
   * provider charged differently cost what it charged.
   */
  searchCostCents: number
  /** What the provider reported charging for this query, in centavos — when it reports. */
  chargedCents?: number | null
  /**
   * The template's price at the moment the query was sent, in centavos.
   *
   * The fallback for what was spent when nobody reported a charge, and named
   * as such: an estimate stamped at request time, not a receipt.
   */
  priceAtRequestCents?: number | null
  summary?: string | null
  details?: string | null
  fileName?: string | null
  filePath?: string | null
  issuedOn?: string | null
  requestedAt?: Date | null
  analyzedAt?: Date | null
  analyzedByAi?: boolean
  /** How this one arrived — `null` while nothing has arrived yet. */
  origin?: CertificateOrigin | null
  /** Why the last attempt produced no document, when one did not. */
  failureKind?: FailureKind | null
  /**
   * The fingerprint of the document as it arrived.
   *
   * Stamped on the dossier, and what anyone re-checking the file years from
   * now compares against. Of the original bytes, not of what was stored: a
   * hash of ciphertext changes with every nonce and proves nothing.
   */
  contentHash?: string | null
  /**
   * When a person has to have this one in hand.
   *
   * Set when the certificate enters the manual queue, from the typical lead
   * time the planilha states for its catalogue item. A queue without a date is
   * a list nobody is late on.
   */
  manualDueAt?: Date | null
  /**
   * Who of the office owns the trip, while it is on a desk.
   *
   * A deadline with nobody's name on it is a list nobody is late on — in an
   * office the work is shared, and "alguém precisa ir buscar" has to become a
   * person.
   */
  assigneeId?: string | null
  /** When the current responsible person was named. */
  assignedAt?: Date | null
  trackingMetadata?: Record<string, string> | null
  /**
   * Whether the reading found the subject's own CPF/CNPJ in the document.
   *
   * The defence against homónimos. A distribution certificate for "João da
   * Silva" in São Paulo comes back with dozens of lawsuits belonging to other
   * people, and a finding attributed to the wrong person is worse than no
   * finding at all: a false red stops a legitimate deal.
   */
  subjectConfirmed?: boolean | null
  /**
   * The person of the office who typed the current result, when one did.
   *
   * Null when a machine answered — the AI reading the paper, or the robot
   * answering in text. Not the same as `analyzedByAi = false`: the worker
   * files its own text verdicts that way, and a robot saying "partes com nome
   * similar" is nobody vouching for anything.
   */
  verdictRecordedBy?: string | null
  /** When the search left for the portal — what a titular's question needs. */
  askedAt?: Date | null
  /**
   * The legal basis, purpose and requester of the last query, as they were
   * when it was made. Null while nothing was ever asked.
   */
  ground?: QueryGround | null
  /**
   * The condition that summoned it, when one did.
   *
   * Null for the base wave. Set for the documents the matrícula brought with
   * it, so the screen can say why a line appeared halfway through the case.
   */
  summonedBy?: string | null
  /**
   * What the reading found, in fields — the kinds the red flags match on.
   *
   * `null` for a certificate read before findings were classified, and for
   * one nobody read: the risk engine then says "conferir" rather than guess.
   */
  reading?: CertificateReading | null
  /**
   * A person's answer to "is this finding the subject's?", when the reading
   * could not give one — the homónimo question settled by hand.
   *
   * Belongs to the paper it was decided on: a new reading drops it.
   */
  linkDecision?: LinkDecision | null
  createdAt: Date
}

/** Who decided whether a finding is the subject's, what, and why — the audit of a homónimo. */
export type LinkDecision = {
  confirmed: boolean
  by: string
  note: string
  at: Date
}

/** The outcome of reading a certificate — what the analyzer hands back. */
export type CertificateVerdict = {
  status: Extract<CertificateStatus, 'clear' | 'warning' | 'adverse'> | 'failed'
  summary: string
  details: string
  issuedOn?: string | null
  /**
   * Whether the subject's document was found in the paper.
   *
   * `null` means nobody could tell — several TJs print no CPF at all — and
   * `null` is read as "not confirmed" everywhere downstream.
   */
  subjectConfirmed?: boolean | null
  /** The findings classified, when the reader classified them. */
  reading?: CertificateReading | null
}

export class Certificate {
  /**
   * Who hears that this line got an answer — its subject, which is the only
   * one that knows which other lines copy it. See `Subject.settleMirrors`.
   */
  private answerListener: (() => void) | null = null
  /** The line this one copies, as its subject finds it — wired by `Subject`, null on a loose line. */
  private copiedFrom: (() => Certificate | null) | null = null

  private constructor(private props: CertificateProps) {}

  static restore(props: CertificateProps): Certificate {
    return new Certificate(props)
  }

  static open(input: {
    id: string
    subjectId: string
    template: CertificateTemplate
    missingFields: PersonalField[]
    /** Set for the certificates no robot fetches — they start on a desk. */
    manualDueAt?: Date | null
    /** The condition that summoned it, for the ones a trigger opened. */
    summonedBy?: string | null
    createdAt: Date
  }): Certificate {
    return new Certificate({
      id: input.id,
      subjectId: input.subjectId,
      name: input.template.name,
      category: input.template.category,
      status: 'pending',
      automated: input.template.automated,
      searchCostCents: input.template.searchCostCents ?? 0,
      summary: null,
      details: null,
      fileName: null,
      filePath: null,
      issuedOn: null,
      requestedAt: null,
      analyzedAt: null,
      analyzedByAi: false,
      origin: null,
      manualDueAt: input.manualDueAt ?? null,
      trackingMetadata: stampsOf(input.template, input.missingFields),
      summonedBy: input.summonedBy ?? null,
      createdAt: input.createdAt
    })
  }

  /**
   * A fresh line for a document a person has to check again.
   *
   * For what no robot fetches — the CNIB, the protest registry — the reissue a
   * sweep cannot queue becomes a task on somebody's desk, with the date it is
   * owed by. The earlier line stays beside it: it is what a decision was
   * taken on, and what the new answer is compared against.
   */
  static recheckOf(
    source: Certificate,
    input: { id: string; at: Date; reason: string; dueAt: Date }
  ): Certificate {
    return new Certificate({
      id: input.id,
      subjectId: source.subjectId,
      name: source.name,
      category: source.category,
      status: 'pending',
      automated: false,
      searchCostCents: source.searchCostCents,
      manualDueAt: input.dueAt,
      // The same document, so the same item and lawsuit — never the source's
      // protocol numbers, which belonged to the earlier request.
      trackingMetadata: source.stamps(),
      summonedBy: input.reason,
      createdAt: input.at
    })
  }

  get id() {
    return this.props.id
  }

  get subjectId() {
    return this.props.subjectId
  }

  get name() {
    return this.props.name
  }

  get category() {
    return this.props.category
  }

  get status() {
    return this.props.status
  }

  get automated() {
    return this.props.automated
  }

  get searchCostCents() {
    return this.props.searchCostCents
  }

  get chargedCents(): number | null {
    return this.props.chargedCents ?? null
  }

  get priceAtRequestCents(): number | null {
    return this.props.priceAtRequestCents ?? null
  }

  /**
   * What this line spent, in centavos, and how that is known.
   *
   * `charged` — the provider said; `priceAtRequest` — the template's price
   * when it was sent, which is what is left when nobody said; `notAsked` —
   * nothing was sent, nothing spent.
   */
  spent(): { cents: number; basis: 'charged' | 'priceAtRequest' | 'notAsked' } {
    if (this.props.chargedCents !== null && this.props.chargedCents !== undefined) {
      return { cents: this.props.chargedCents, basis: 'charged' }
    }

    if (this.props.priceAtRequestCents !== null && this.props.priceAtRequestCents !== undefined) {
      return { cents: this.props.priceAtRequestCents, basis: 'priceAtRequest' }
    }

    return { cents: 0, basis: 'notAsked' }
  }

  /**
   * What was actually paid for this query — the receipt beats the price table.
   *
   * Said by the provider (the worker writes it when a portal reports a price)
   * or by the person who paid the fee at the cartório and attached the result.
   */
  recordCharge(amount: Money): void {
    this.props = { ...this.props, chargedCents: Math.max(0, amount.cents) }
  }

  get summary() {
    return this.props.summary ?? null
  }

  get details() {
    return this.props.details ?? null
  }

  get fileName() {
    return this.props.fileName ?? null
  }

  get filePath() {
    return this.props.filePath ?? null
  }

  get issuedOn() {
    return this.props.issuedOn ?? null
  }

  get requestedAt() {
    return this.props.requestedAt ?? null
  }

  get analyzedAt() {
    return this.props.analyzedAt ?? null
  }

  get analyzedByAi() {
    return this.props.analyzedByAi ?? false
  }

  get origin() {
    return this.props.origin ?? null
  }

  /**
   * The origin, or the one a document with no stated origin must have had.
   *
   * Rows the worker filed before it wrote `origin` carry a file and a null:
   * the robot was the only path that predates the field — the same rule
   * `attachFile` applies. Without this the dossier said "Não obtida" about a
   * certificate whose PDF was bound right there in the annex.
   */
  knownOrigin(evidence: { robotAnswered?: boolean } = {}): CertificateOrigin | null {
    if (this.props.origin) return this.props.origin

    if (this.props.filePath) return 'scraper'

    // A verdict with no file is an answer read off the page. When a robot job
    // for this certificate finished, that answer is the robot's (DIL-052);
    // the caller who holds the jobs says so.
    return evidence.robotAnswered && this.isResolved() ? 'scraper' : null
  }

  /**
   * The origin in words, and the honest words when it is unknown.
   *
   * A verdict whose origin nobody wrote down was still obtained: "Não obtida"
   * about it is false. Before the field existed nothing recorded who brought
   * it, so that is what it says.
   */
  originLabel(evidence: { robotAnswered?: boolean } = {}): string {
    const origin = this.knownOrigin(evidence)

    if (origin) return ORIGIN_LABEL[origin]

    return this.isResolved() ? UNRECORDED_ORIGIN_LABEL : NOT_OBTAINED_LABEL
  }

  get failureKind() {
    return this.props.failureKind ?? null
  }

  get contentHash() {
    return this.props.contentHash ?? null
  }

  get manualDueAt() {
    return this.props.manualDueAt ?? null
  }

  get assigneeId(): string | null {
    return this.props.assigneeId ?? null
  }

  get assignedAt(): Date | null {
    return this.props.assignedAt ?? null
  }

  get trackingMetadata() {
    return this.props.trackingMetadata ?? null
  }

  /** The catalogue item this line was opened for, when a trigger stamped one. */
  get catalogItemId(): string | null {
    return this.props.trackingMetadata?.[CATALOG_ITEM_KEY] ?? null
  }

  /** The lawsuit this line was opened for, when it was asked per lawsuit. */
  get lawsuitNumber(): string | null {
    return this.props.trackingMetadata?.[LAWSUIT_KEY] ?? null
  }

  /** What was stamped on the line when it was opened. */
  private stamps(): Record<string, string> | null {
    const stamps: Record<string, string> = {}

    if (this.catalogItemId) stamps[CATALOG_ITEM_KEY] = this.catalogItemId
    if (this.lawsuitNumber) stamps[LAWSUIT_KEY] = this.lawsuitNumber

    return Object.keys(stamps).length ? stamps : null
  }

  /** The condition that opened it — null for everything owed from the start. */
  get summonedBy() {
    return this.props.summonedBy ?? null
  }

  /** What the reading found, in fields — `null` when nobody classified it. */
  get reading(): CertificateReading | null {
    return this.props.reading ?? null
  }

  /** A person's decision on whether the finding is the subject's — `null` while nobody decided. */
  get linkDecision(): LinkDecision | null {
    const holder = this.linkHolder()

    if (holder !== this) return holder.linkDecision

    return this.props.linkDecision ?? null
  }

  /**
   * Tells this line where the answer it may copy comes from.
   *
   * Wired by the subject, which alone knows the pairs: a line holding a copy
   * of its source's answer is the source's paper, and asks the source whose
   * that paper is.
   */
  followSource(source: () => Certificate | null): void {
    this.copiedFrom = source
  }

  /**
   * The line that answers "is this finding the subject's?" for this one.
   *
   * A copy is the source's paper: its link is the source's, read — never
   * copied — so a decision on the source, either way, is every copy's too
   * (DIL-122). A line with an answer of its own holds its own.
   */
  linkHolder(): Certificate {
    if (this.props.origin !== 'mirror') return this

    const source = this.copiedFrom?.() ?? null

    return source && source !== this ? source : this
  }

  /**
   * A person settles whether the finding is the subject's.
   *
   * The way out of "não confirmado" when the paper carries no CPF: somebody
   * checks the lawsuit itself and says "é dele" or "é homônimo", with the
   * reason, and the decision is what every reader of the link answers from
   * then on. Only a paper with a finding has a link to decide.
   */
  decideLink(input: { confirmed: boolean; by: string; note: string; at: Date }): Result<void, AppError> {
    const holder = this.linkHolder()

    // Decided on the copy's row, it is still the paper's decision.
    if (holder !== this) return holder.decideLink(input)

    if (this.props.status !== 'adverse' && this.props.status !== 'warning') {
      return err(invalidInput('Só há vínculo a decidir numa certidão com apontamento.'))
    }

    const note = input.note.trim()

    if (!note) return err(invalidInput('Diga por que o apontamento é ou não do pesquisado — a decisão fica na trilha.'))

    this.props = {
      ...this.props,
      linkDecision: { confirmed: input.confirmed, by: input.by, note, at: input.at }
    }

    return ok()
  }

  /** When the search left for the portal. */
  get askedAt() {
    return this.props.askedAt ?? null
  }

  /** Why, and at whose request, the last query was made — frozen at the time. */
  get ground(): QueryGround | null {
    return this.props.ground ?? null
  }

  /**
   * The raw document goes; what it was stays.
   *
   * The fingerprint, the reading and the dates are the case record — the
   * dossier still points at them. Only the bytes have a shorter life.
   */
  discardFile(): void {
    if (!this.props.filePath) return

    this.props = { ...this.props, fileName: null, filePath: null }
  }

  /** Whether the subject's own document was found in the paper. */
  get subjectConfirmed() {
    return this.props.subjectConfirmed ?? null
  }

  /**
   * Whether a finding here can be pinned on this subject at all.
   *
   * Only an adverse or warning reading raises the question: nothing found is
   * nothing to attribute to the wrong person. An unconfirmed link never stops
   * being reported — it stops being reported *as settled*.
   *
   * Who answered the question matters. An explicit `false` is the reading
   * saying the paper is somebody else's, and it always counts. A `null` is
   * "nobody could tell", and that only stands when nobody of the office looked:
   * a machine read the paper, or the robot answered in text. Whoever brought
   * it back from the counter, or typed the result, is the one who checked
   * whose name is on it.
   */
  hasUnconfirmedLink(): boolean {
    if (this.props.status !== 'adverse' && this.props.status !== 'warning') return false

    const holder = this.linkHolder()

    if (holder !== this) return holder.hasUnconfirmedLink()
    // Somebody already answered it, either way: nothing is left waiting.
    if (this.props.linkDecision) return false

    return this.linkConfirmed !== true
  }

  /**
   * Whether the paper is the subject's, as far as anybody can answer.
   *
   * What the reading said when it said something; otherwise `true` when a
   * person of the office looked — they typed the result, or brought the paper
   * in themselves — and `null` when only a machine did. `analyzedByAi` cannot
   * answer this: the worker files the robot's text verdicts with it `false`.
   *
   * A refusal is the órgão declining to issue for the document it was asked
   * about, so it is the subject's by construction.
   */
  get linkConfirmed(): boolean | null {
    const holder = this.linkHolder()

    if (holder !== this) return holder.linkConfirmed
    if (this.props.linkDecision) return this.props.linkDecision.confirmed
    if (this.subjectConfirmed !== null) return this.subjectConfirmed
    if (this.props.verdictRecordedBy) return true
    if (this.props.failureKind === 'refused') return true

    const origin = this.knownOrigin()

    return origin && OFFICE_BROUGHT_ORIGINS.includes(origin) ? true : null
  }

  /** The person of the office who typed the current result — null when a machine answered. */
  get verdictRecordedBy(): string | null {
    return this.props.verdictRecordedBy ?? null
  }

  get createdAt() {
    return this.props.createdAt
  }

  /** Nothing left to do: the analysis reached a final answer. */
  isResolved(): boolean {
    return this.props.status === 'clear' || this.props.status === 'warning' || this.props.status === 'adverse'
  }

  /** Already carries a document, however it got here. */
  hasFile(): boolean {
    return Boolean(this.props.filePath)
  }

  /**
   * Worth putting in the automatic queue: never ran, failed and can retry, or
   * waited for an e-mail that did not come.
   *
   * `at` is what lets a line come back: without it, only what is due right
   * away answers yes — the button a person presses, which never waits.
   */
  isQueueable(at?: Date, options: { anticipate?: boolean } = {}): boolean {
    if (!this.props.automated || this.hasFile()) return false

    // A person asking now — "tentar agora", after fixing an account — goes
    // ahead of the pause the sweep would wait: that is what the button is for.
    // A refusal of the data too: the person may have just fixed the record or
    // the account. Only the body's own refusal is an answer no click changes.
    if (options.anticipate && this.props.status === 'failed' && this.props.failureKind !== 'refused') return true

    // The body took the request and mails the paper: asking again before the
    // wait is over paid the provider for the same receipt every five minutes.
    if (this.isAwaitingEmail()) return Boolean(at) && at!.getTime() - this.props.requestedAt!.getTime() >= EMAIL_WAIT_MS

    if (this.props.status === 'pending') return true

    // A failure only goes back in the queue when going back could change the
    // answer. A missing field needs a person; a refusal already is the answer.
    return this.isRetryable(at) || this.dataRecheckDue(at)
  }

  /**
   * A refusal of the data is checked once more, a day later.
   *
   * Sources call "invalid" what clears by itself — the TJSP's e-mail used
   * twice in half an hour, a timeout behind a 4xx — and a refusal filed as
   * final kept a line dead for good. Once, not every day: a CPF the source
   * does not know will not know it tomorrow either, and each ask is paid.
   */
  dataRecheckDue(at?: Date): boolean {
    const when = this.dataRecheckAt()

    return Boolean(at && when && when.getTime() <= at.getTime())
  }

  /** When the refusal gets its one more look — `null` when it already had it, or is no refusal. */
  dataRecheckAt(): Date | null {
    if (this.props.status !== 'failed' || this.props.failureKind !== 'missingData') return null
    if (this.props.trackingMetadata?.[DATA_RECHECKED_KEY] || !this.props.analyzedAt) return null

    return new Date(this.props.analyzedAt.getTime() + DATA_RECHECK_AFTER_MS)
  }

  /** Stamps that the refusal was given its one more look — the sweep calls this when it sends it. */
  markDataRecheck(at: Date): void {
    this.props = {
      ...this.props,
      trackingMetadata: { ...(this.props.trackingMetadata ?? {}), [DATA_RECHECKED_KEY]: at.toISOString() }
    }
  }

  /**
   * The paper a robot, a provider or a court's e-mail brought, read as not
   * being the certificate — a receipt, an order form, a page of the portal.
   *
   * Kept as `failed` with the paper on it, the line was finished: the queue
   * does not fetch what has a file, and the reader does not re-read what it
   * read. A machine that brought the wrong paper can bring the right one, so
   * the paper is set aside (its path stays in the tracking, for the record)
   * and the line fails as technical — due again by the same clock as any
   * other failure.
   */
  setAsideFetchedPaper(reason: string, at: Date): void {
    const tracking = {
      ...(this.props.trackingMetadata ?? {}),
      ...(this.props.filePath ? { setAsidePath: this.props.filePath } : {}),
      ...(this.props.fileName ? { setAsideName: this.props.fileName } : {})
    }

    this.props = { ...this.props, fileName: null, filePath: null, contentHash: null, trackingMetadata: tracking }
    this.fail(reason, at, 'technical')
  }

  /**
   * A paper a machine brought that was read and failed — a receipt, a portal
   * page — kept on the line from before the reading set such papers aside.
   * Nothing moved it: the queue skips lines with a file and the reader skips
   * what it read. The sweep sets it aside (`setAsideFetchedPaper`).
   */
  holdsUnusablePaper(): boolean {
    return (
      this.props.status === 'failed' &&
      this.hasFile() &&
      Boolean(this.props.analyzedAt) &&
      this.wasFetchedByMachine() &&
      (this.props.failureKind ?? 'technical') === 'technical'
    )
  }

  /** Whether the paper on this line came from a machine — and a machine can go for another. */
  wasFetchedByMachine(): boolean {
    return this.props.origin === 'scraper' || this.props.origin === 'provider' || this.props.origin === 'email'
  }

  /** The reading model could not answer: counted, so the next attempt waits instead of paying every pass. */
  recordReadingFailure(at: Date): void {
    this.props = {
      ...this.props,
      trackingMetadata: {
        ...(this.props.trackingMetadata ?? {}),
        [READING_FAILURES_KEY]: String(this.readingFailures() + 1),
        [READING_FAILED_AT_KEY]: at.toISOString()
      }
    }
  }

  /** Whether the reading can be asked now: it never failed, or the growing pause is over. */
  readingDue(at: Date): boolean {
    const failures = this.readingFailures()
    const last = this.props.trackingMetadata?.[READING_FAILED_AT_KEY]

    if (!failures || !last) return true

    return at.getTime() - new Date(last).getTime() >= Math.min(12 * HOUR_MS, 15 * 60 * 1000 * 2 ** failures)
  }

  private readingFailures(): number {
    return Number(this.props.trackingMetadata?.[READING_FAILURES_KEY] ?? 0) || 0
  }

  /** The person's registry was asked about the subject of this line. */
  stampRegistryAsked(at: Date): void {
    this.props = { ...this.props, trackingMetadata: { ...(this.props.trackingMetadata ?? {}), [REGISTRY_ASKED_KEY]: at.toISOString() } }
  }

  registryAsked(): boolean {
    return Boolean(this.props.trackingMetadata?.[REGISTRY_ASKED_KEY])
  }

  /** The body took the request and the certificate is coming by e-mail. */
  isAwaitingEmail(): boolean {
    return this.props.status === 'pending' && Boolean(this.props.requestedAt) && !this.hasFile()
  }

  /** Somebody has to go to the portal for this one — or copy it from a sibling. */
  needsAnswer(): boolean {
    return this.props.status === 'pending' || this.props.status === 'running' || this.props.status === 'failed'
  }

  /**
   * A provider's flag said something is there, and nobody read what.
   *
   * The flag answers the line — the screen and the traffic light work without
   * a model — but a flag carries no lawsuit number, no amount, no débito. Left
   * unread, the risk matrix can only say "to verify" and caps the line at
   * yellow. A clean flag needs no reading, the system's own proof of a query
   * is not a document worth one, and a result a person typed is theirs.
   */
  awaitsFindings(): boolean {
    return (
      this.props.origin === 'provider' &&
      this.hasFile() &&
      !this.analyzedByAi &&
      !this.props.verdictRecordedBy &&
      !this.reading &&
      !this.queryRecord &&
      (this.props.status === 'adverse' || this.props.status === 'warning')
    )
  }

  /**
   * What the model read in a document a provider already answered.
   *
   * The findings are the model's; the verdict is the worse of the two. A model
   * misreading a scanned page must not clear a debt the source itself flagged,
   * and a worse reading is news the flag missed.
   */
  recordFindings(verdict: CertificateVerdict, at: Date): VerdictChange {
    if (verdict.status !== 'failed' && changeBetween(this.props.status, verdict.status).worsened) {
      const change = this.recordVerdict(verdict, at, true)

      this.props = { ...this.props, origin: 'provider' }

      return change
    }

    this.props = {
      ...this.props,
      reading: verdict.reading ?? null,
      subjectConfirmed: verdict.subjectConfirmed ?? this.props.subjectConfirmed ?? null,
      issuedOn: this.props.issuedOn ?? verdict.issuedOn ?? null,
      analyzedAt: at,
      analyzedByAi: true
    }

    return changeBetween(this.props.status, this.props.status)
  }

  /**
   * No robot will bring this one: a person has to.
   *
   * It is not an error and not a failure — most of the catalogue is like this
   * by nature, and a case that treats it as a problem will look broken while
   * working exactly as designed.
   */
  needsHumanWork(): boolean {
    return !this.props.automated && !this.hasFile() && !this.isResolved()
  }

  /** Already on somebody's desk, with a date attached. */
  isInManualQueue(): boolean {
    return this.needsHumanWork() && Boolean(this.props.manualDueAt)
  }

  /**
   * Puts it on a person's desk with a deadline.
   *
   * The deadline is an argument rather than a calculation because it comes
   * from the catalogue — the planilha states a typical lead time per item, and
   * the domain has no business guessing one.
   */
  enterManualQueue(dueAt: Date): void {
    if (!this.needsHumanWork()) return

    this.props = { ...this.props, manualDueAt: dueAt }
  }

  /**
   * Names who goes and gets it — or, with `null`, takes the name off.
   *
   * Only a line that needs human work can be given to somebody: a robot's line
   * has no trip to own, and an answered one has nothing left to do. Taking the
   * name off is always allowed, so a stale assignment never gets stuck on a
   * line that has since left the desk.
   */
  assignTo(input: { assigneeId: string | null; at: Date }): Result<void, AppError> {
    if (input.assigneeId === null) {
      this.props = { ...this.props, assigneeId: null, assignedAt: null }

      return ok()
    }

    if (!this.needsHumanWork()) return err(certificateNotManual())

    this.props = { ...this.props, assigneeId: input.assigneeId, assignedAt: input.at }

    return ok()
  }

  /**
   * Past its date, counted in days.
   *
   * What is due today is not late — the same rule the rest of the product uses
   * for validity, and the one a person would use out loud.
   */
  isManualOverdue(now: Date): boolean {
    const due = this.props.manualDueAt

    if (!due || !this.needsHumanWork()) return false

    const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

    return startOfDay(now).getTime() > startOfDay(due).getTime()
  }

  /**
   * The search left for the portal.
   *
   * The date is kept because "quando vocês consultaram o CPF de fulano" is a
   * question a titular is entitled to ask, and until now the moment a robot
   * was sent existed only in a job row that gets cleaned up.
   */
  markRunning(at?: Date): void {
    this.props = { ...this.props, status: 'running', askedAt: at ?? this.props.askedAt ?? null }
  }

  /**
   * A query is being made: records when, under which ground, and for whom.
   *
   * The ground is copied, not referenced. The case's basis and purpose can be
   * edited afterwards, and an edit must not rewrite why this CPF was looked up
   * on that day. Every new query replaces the snapshot — it is a new query,
   * made under whatever the case says now; the earlier ones stay in the trail.
   */
  recordQuery(input: { ground: LegalGround; askedBy: QueryRequester; at: Date }): void {
    this.props = {
      ...this.props,
      askedAt: input.at,
      // The price is stamped with the query: it is what this query is assumed
      // to have cost until the provider says otherwise.
      priceAtRequestCents: this.props.searchCostCents,
      ground: { basis: input.ground.basis, purpose: input.ground.purpose, askedBy: input.askedBy, backfilled: false }
    }
  }

  /**
   * No robot reaches this document here: it goes on a person's desk.
   *
   * The template knows a scraper, but no provider issues the line and the
   * robots are off. The line stops being one the queue offers again, carries
   * the catalogue's deadline, and keeps why, so the person on duty knows it
   * is not a failure to retry.
   */
  routeToManualQueue(input: { dueAt: Date; reason: string }): void {
    this.props = {
      ...this.props,
      // On a desk nothing is processing: a line left "running" here was never
      // found again by the routine that hands desk lines back to a provider.
      status: this.props.status === 'running' ? 'pending' : this.props.status,
      automated: false,
      manualDueAt: input.dueAt,
      trackingMetadata: { ...(this.props.trackingMetadata ?? {}), coverage: input.reason }
    }
  }

  /**
   * A contracted provider will fetch this one.
   *
   * A line opened before the provider was contracted sits on a desk; once a
   * provider issues it there, it leaves the desk and becomes a line the
   * automatic queue offers — nobody should go to a counter for a paper an API
   * hands over.
   */
  assignToProvider(input: { costCents?: number } = {}): void {
    const tracking = { ...(this.props.trackingMetadata ?? {}) }

    // Why it sat on a desk no longer holds.
    delete tracking.coverage

    const stuck = this.props.status === 'failed' || this.props.status === 'running'

    this.props = {
      ...this.props,
      automated: true,
      // A failure from a robot that is gone, or a search left "processing" on
      // a desk, says nothing about what the provider will answer: it starts over.
      ...(stuck ? { status: 'pending' as const, summary: null, details: null, failureKind: null } : {}),
      manualDueAt: null,
      // Off the desk, nobody is responsible for a trip that will not happen.
      assigneeId: null,
      assignedAt: null,
      searchCostCents: input.costCents ?? this.props.searchCostCents,
      trackingMetadata: Object.keys(tracking).length ? tracking : null
    }
  }

  /** The portal took the request and will mail the certificate later. */
  markAwaitingEmail(at: Date, tracking: Record<string, string>): void {
    this.props = {
      ...this.props,
      status: 'pending',
      summary: 'Pedido aceito pelo portal. A certidão será enviada por email.',
      details: null,
      requestedAt: at,
      trackingMetadata: { ...(this.props.trackingMetadata ?? {}), ...tracking }
    }
  }

  attachFile(input: {
    fileName: string
    filePath: string
    at: Date
    origin?: CertificateOrigin
    contentHash?: string
  }): void {
    this.props = {
      ...this.props,
      fileName: input.fileName,
      filePath: input.filePath,
      contentHash: input.contentHash ?? this.props.contentHash ?? null,
      requestedAt: null,
      // A real document replaces the proof of a query that had none.
      trackingMetadata: withoutQueryRecord(this.props.trackingMetadata ?? null),
      // Whoever brought it says so; a file with no stated origin came from the
      // robot, because that is the only path that predates this field.
      origin: input.origin ?? this.props.origin ?? 'scraper',
      // It is nobody's pending task once the document is in.
      manualDueAt: null,
      status: this.props.status === 'failed' ? 'pending' : this.props.status
    }
  }

  /**
   * A contracted provider handed the certificate over.
   *
   * With a verdict, the line is answered and the reading routine leaves it
   * alone — the provider read the source's own answer, and a model reading the
   * PDF again would only pay to say the same. Without one, it goes back to
   * `pending` with the document in, which is exactly what the reading routine
   * picks up.
   *
   * What was asked and of whom is stamped on the line: a sibling line asking
   * the same product reuses this answer instead of paying for it again.
   *
   * With `queryRecord`, the file is the system's proof of the query, not a
   * certificate — the line says so for as long as it holds it.
   */
  receiveFromProvider(input: {
    file: { fileName: string; filePath: string; contentHash: string } | null
    verdict: CertificateVerdict | null
    summary: string
    details: string
    issuance: { provider: string; product: string; receipt: string | null }
    queryRecord?: QueryRecordStamp | null
    at: Date
  }): VerdictChange | null {
    if (input.file) this.attachFile({ ...input.file, origin: 'provider', at: input.at })

    this.props = {
      ...this.props,
      origin: 'provider',
      failureKind: null,
      requestedAt: null,
      manualDueAt: null,
      trackingMetadata: {
        ...(this.props.trackingMetadata ?? {}),
        issuedBy: input.issuance.provider,
        issuedProduct: input.issuance.product,
        ...(input.issuance.receipt ? { providerReceipt: input.issuance.receipt } : {}),
        ...(input.queryRecord ? queryRecordStamps(input.queryRecord) : {})
      }
    }

    if (input.verdict) {
      const change = this.recordVerdict(input.verdict, input.at, false)

      this.props = { ...this.props, origin: 'provider' }

      return change
    }

    this.props = { ...this.props, status: 'pending', summary: input.summary.slice(0, 500), details: input.details }

    return null
  }

  /** The provider product that answered this line, when one did. */
  get issuedProduct(): string | null {
    return this.props.trackingMetadata?.issuedProduct ?? null
  }

  /**
   * The answer came as data and the file is the system's proof of the query —
   * not a certificate. `null` when the line holds a real document, or none.
   */
  get queryRecord(): QueryRecordStamp | null {
    const missingDocument = this.props.trackingMetadata?.[QUERY_RECORD_KEY]

    if (!missingDocument) return null

    return { missingDocument, verifyAt: this.props.trackingMetadata?.[VERIFY_AT_KEY] ?? null }
  }

  /** Records what the analyzer read in the document. */
  /**
   * Records the answer, and says how it differs from the one before it.
   *
   * The change is returned rather than merely applied because a certificate
   * that comes back adverse *after having come back clean* is news about a
   * case somebody already decided on — and nothing downstream can tell that
   * apart once the old status has been overwritten.
   */
  recordVerdict(verdict: CertificateVerdict, at: Date, byAi: boolean): VerdictChange {
    const change = changeBetween(this.props.status, verdict.status)

    this.props = {
      ...this.props,
      status: verdict.status,
      summary: verdict.summary,
      details: verdict.details,
      issuedOn: verdict.issuedOn ?? this.props.issuedOn ?? null,
      subjectConfirmed: verdict.subjectConfirmed ?? null,
      // A new verdict replaces the old reading whole: findings classified from
      // the previous document say nothing about this one.
      reading: verdict.reading ?? null,
      // Decided on the previous paper; this one has not been looked at yet.
      linkDecision: null,
      analyzedAt: at,
      analyzedByAi: byAi,
      // A machine's answer replaces whoever typed the one before.
      verdictRecordedBy: null,
      // An answer of its own: whatever this line said as a copy of a sibling,
      // it no longer does, and the next copy must not overwrite it.
      origin: this.props.origin === 'mirror' ? null : this.props.origin
    }

    this.answered()

    return change
  }

  /**
   * Hands the subject the moment this line is answered.
   *
   * Every way an answer arrives — the robot's reading, a person's upload, the
   * mailbox, the counterparty, a typed result, a reissue — ends in
   * `recordVerdict` or `refuse`, so the copy to the mirrors hangs here and not
   * on each caller: the reading routine was the only one that copied, and a
   * federal certificate attached by hand left its TRF mirror waiting forever
   * (DIL-122).
   */
  listenForAnswers(listener: () => void): void {
    this.answerListener = listener
  }

  private answered(): void {
    this.answerListener?.()
  }

  /**
   * A result a person of the office typed in.
   *
   * Recorded with who typed it: that person is the one vouching the finding is
   * the subject's, which no machine answer can do.
   */
  recordTypedResult(verdict: CertificateVerdict, at: Date, recordedBy: string): VerdictChange {
    // A document already in keeps the origin of whoever brought it; an answer
    // nobody fetched was obtained by the person typing it (DIL-052).
    // A line that only held a sibling's copy gets an answer of its own here.
    const known = this.knownOrigin()
    const origin = known && known !== 'mirror' ? known : 'manual'
    const change = this.recordVerdict(verdict, at, false)

    this.props = { ...this.props, origin, verdictRecordedBy: recordedBy }

    return change
  }

  /**
   * Records that the search produced no document, and why.
   *
   * The default is `technical` because that is what an unqualified failure has
   * always meant here — an old caller that does not say is describing a portal
   * that broke, which is the one kind worth retrying.
   */
  fail(reason: string, at: Date, kind: FailureKind = 'technical'): void {
    const rounds = kind === 'technical' ? this.technicalRounds() + 1 : this.technicalRounds()

    this.props = {
      ...this.props,
      status: 'failed',
      summary: reason,
      details: null,
      failureKind: kind,
      analyzedAt: at,
      trackingMetadata: { ...(this.props.trackingMetadata ?? {}), [TECHNICAL_ROUNDS_KEY]: String(rounds) }
    }

    // A failure takes back whatever answer the line had, and its mirrors
    // must not keep saying it.
    this.answered()
  }

  /** How many times the queue gave up on this line for a technical reason. */
  technicalRounds(): number {
    return Number(this.props.trackingMetadata?.[TECHNICAL_ROUNDS_KEY] ?? 0) || 0
  }

  /**
   * Back to the queue once the record it failed for has changed.
   *
   * A line that failed for a missing field could only wait for a person; once
   * the person edits the record, the next search may well succeed.
   */
  reopenForNewData(): void {
    if (this.props.status !== 'failed' || this.props.failureKind !== 'missingData') return

    this.props = { ...this.props, status: 'pending', summary: null, details: null, failureKind: null, analyzedAt: null }
  }

  /**
   * The órgão answered and declined to issue.
   *
   * This is not a failure and must not be stored as one: the subject has
   * debts, which is precisely what the diligence went to find out. It is an
   * adverse result, and the queue must leave it alone — going back would pay
   * for another search to be told the same thing.
   */
  refuse(reason: string, at: Date): void {
    this.props = {
      ...this.props,
      status: 'adverse',
      summary: reason,
      failureKind: 'refused',
      analyzedAt: at
    }

    this.answered()
  }

  /**
   * Whether the failure waits on somebody fixing an external account.
   *
   * A balance that ran out, credentials that expired, a contract with
   * pendencies: retrying burns a search to be told the same thing. Decided
   * here, from the diagnostic, so the screen can warn without ever being
   * handed the diagnostic itself.
   */
  needsExternalFix(): boolean {
    if (this.props.status !== 'failed') return false

    const diagnostic = (this.props.summary ?? '').toLowerCase()

    return NEEDS_EXTERNAL_FIX.some(phrase => diagnostic.includes(phrase))
  }

  /**
   * Whether going back to the portal could produce a different answer — now.
   *
   * A source that broke this many sweeps in a row is not having a bad minute,
   * and sending the line back every five minutes for days paid for nothing.
   * Nor is it gone for good: past the quick rounds the line waits longer and
   * longer — an hour, six, then a day — and goes again. "Never" was the dead
   * end that left a SEFAZ down for one afternoon unasked for ever.
   */
  isRetryable(at?: Date): boolean {
    if (this.props.status !== 'failed') return false
    if ((this.props.failureKind ?? 'technical') !== 'technical') return false

    const rounds = this.technicalRounds()

    // An account, a credential or a certificate missing does not fix itself in
    // five minutes: it waits its pause from the first failure, like the rest.
    if (rounds < MAX_TECHNICAL_ROUNDS && !this.needsExternalFix()) return true
    if (!at) return false

    return this.nextAttemptAt()!.getTime() <= at.getTime()
  }

  /**
   * When the queue goes back for this line by itself, or `null` when it does
   * not — answered, waiting on a person, or simply due already.
   *
   * What the screen prints as "tenta de novo às 14:30": the same arithmetic
   * the sweep runs, so the promise and the behaviour cannot drift apart.
   */
  nextAttemptAt(): Date | null {
    if (this.isAwaitingEmail()) return new Date(this.props.requestedAt!.getTime() + EMAIL_WAIT_MS)
    if (this.props.status !== 'failed' || (this.props.failureKind ?? 'technical') !== 'technical') return null

    const rounds = this.technicalRounds()

    if ((rounds < MAX_TECHNICAL_ROUNDS && !this.needsExternalFix()) || !this.props.analyzedAt) return null

    return new Date(this.props.analyzedAt.getTime() + patienceAfter(rounds))
  }

  /**
   * Takes over the answer another certificate already got.
   *
   * The same query under two names — the CJF covers every federal court, the
   * CNDT covers the whole country — and the portal was visited once. What the
   * copy keeps of its own is the identity: its id, its name, and the file it
   * points at, which is the same document on disk.
   */
  copyAnswerFrom(source: Certificate, at: Date): void {
    this.props = {
      ...this.props,
      status: source.status,
      summary: source.summary,
      details: source.details,
      fileName: source.fileName,
      filePath: source.filePath,
      issuedOn: source.issuedOn,
      requestedAt: source.requestedAt,
      analyzedAt: at,
      analyzedByAi: source.analyzedByAi,
      // The document is literally the sibling's file, so it carries the same
      // fingerprint — that is what makes the copy verifiable at all.
      contentHash: source.contentHash,
      // The only query was the sibling's, so its moment and its ground are the
      // answer to "why was this looked up" for the copy too.
      askedAt: source.askedAt,
      ground: source.ground,
      reading: source.reading,
      // What the paper's reading said. Whose it is — with any person's
      // decision — is read from the source while this holds its copy
      // (`linkHolder`), never frozen here (DIL-122).
      subjectConfirmed: source.subjectConfirmed,
      verdictRecordedBy: null,
      // The document is the sibling's; saying it came from the robot would
      // claim a trip to a portal that never happened for this one.
      origin: 'mirror',
      manualDueAt: null,
      // A copy of a proof of query is no more a certificate than the original.
      trackingMetadata: source.queryRecord
        ? { ...withoutQueryRecord(this.props.trackingMetadata ?? null), ...queryRecordStamps(source.queryRecord) }
        : withoutQueryRecord(this.props.trackingMetadata ?? null)
    }
  }

  /**
   * Drops a copy whose source no longer has the answer it was copied from.
   *
   * The source can step back — the CJF's receipt first filed as a document,
   * then read as "not a certificate" and failed — and a copy kept from before
   * went on saying "warning", tripping a red flag over a paper that does not
   * exist. The mirror goes back to waiting for its source.
   */
  forgetCopy(): void {
    if (this.props.origin !== 'mirror') return

    this.props = {
      ...this.props,
      status: 'pending',
      summary: null,
      details: null,
      fileName: null,
      filePath: null,
      issuedOn: null,
      analyzedAt: null,
      analyzedByAi: false,
      contentHash: null,
      reading: null,
      subjectConfirmed: null,
      origin: null,
      trackingMetadata: withoutQueryRecord(this.props.trackingMetadata ?? null)
    }
  }

  /** Whether this already says what the source says — nothing left to copy. */
  matchesAnswerOf(source: Certificate): boolean {
    return (
      this.props.status === source.status &&
      (this.props.filePath ?? null) === source.filePath &&
      (this.props.summary ?? null) === source.summary
    )
  }

  /** The personal fields this certificate is still waiting on. */
  /** Why the line sits on a desk, as the routing said when it put it there. */
  get deskReason(): string | null {
    return this.props.trackingMetadata?.coverage ?? null
  }

  get missingFields(): PersonalField[] {
    const raw = this.props.trackingMetadata?.missingFields

    return raw ? (raw.split(',').filter(Boolean) as PersonalField[]) : []
  }

  setMissingFields(fields: PersonalField[]): void {
    const tracking = { ...(this.props.trackingMetadata ?? {}) }

    if (fields.length) tracking.missingFields = fields.join(',')
    else delete tracking.missingFields

    this.props = {
      ...this.props,
      trackingMetadata: Object.keys(tracking).length ? tracking : null,
      // What was only stuck for lack of data goes back into the queue.
      summary: !fields.length && this.props.status === 'pending' && !this.hasFile() ? null : this.props.summary
    }
  }

  /** Strips everything a person could be recognized by. Irreversible. */
  anonymize(): void {
    this.props = {
      ...this.props,
      status: 'purged',
      summary: null,
      details: null,
      fileName: null,
      filePath: null,
      trackingMetadata: null,
      reading: null,
      analyzedByAi: false,
      verdictRecordedBy: null
    }
  }

  toJSON(): CertificateProps {
    return { ...this.props }
  }
}

// ─── Subject ────────────────────────────────────────────────────────────────

export type PersonalData = {
  nationalId: string
  birthDate: string
  motherName: string
  fatherName: string
  gender: '' | 'M' | 'F'
  nationality: string
  birthCountry: string
  birthState: string
  birthCity: string
  councilState: string
  /**
   * Where the person lives — the state and city whose courts, treasury and
   * city hall the state and municipal certificates are asked of when the case
   * has no property to answer for them (the CPF search never has one).
   */
  homeState: string
  homeCity: string
}

/** What a registry knows of a person by the CPF alone — the fields courts and treasuries ask for. */
export type RegisteredPerson = Partial<Pick<PersonalData, 'birthDate' | 'motherName' | 'gender' | 'homeState' | 'homeCity'>>

/**
 * What of a registry's answer the record can take: a UF that is a state, the
 * city only with it, and a gender the courts' forms know.
 *
 * The address comes from an enrichment base, not the Receita — a wrong one
 * must fail quietly here, never stop the person being registered.
 */
export const registeredPersonOf = (found: RegisteredPerson | null): RegisteredPerson => {
  if (!found) return {}

  const homeState = (found.homeState ?? '').trim().toUpperCase()
  const validHome = STATES.has(homeState)
  const gender = (found.gender ?? '').trim().toUpperCase().charAt(0)

  return {
    ...(found.birthDate?.trim() ? { birthDate: found.birthDate.trim() } : {}),
    ...(found.motherName?.trim() ? { motherName: found.motherName.trim() } : {}),
    ...(gender === 'M' || gender === 'F' ? { gender } : {}),
    ...(validHome ? { homeState } : {}),
    ...(validHome && found.homeCity?.trim() ? { homeCity: found.homeCity.trim() } : {})
  }
}

/** The fields a person's registry can fill — the ones whose absence holds a line or a territory. */
const REGISTRY_FIELDS: readonly (keyof RegisteredPerson)[] = ['birthDate', 'motherName', 'gender', 'homeState']

/**
 * Where a property subject is registered.
 *
 * Empty strings for the other two kinds of subject: a person has no registry
 * office. Kept flat rather than as a nested value object because the column
 * layout is flat and the aggregate is restored straight from a row.
 */
export type RegistrationData = {
  registryOffice: string
  city: string
  state: string
}

export const emptyRegistrationData = (): RegistrationData => ({ registryOffice: '', city: '', state: '' })

/** The two doors people come into a case by, besides being typed in. */
export type SubjectExpansion = 'qsa' | 'matricula'

export type SubjectProps = {
  id: string
  diligenceId: string
  type: SubjectType
  /**
   * What the public registry says about a company subject.
   *
   * `null` for people, and for companies looked up before this existed. It is
   * third-party personal data — the partners' names and documents — so it is
   * encrypted at rest like the rest of it.
   */
  profile: CompanyProfile | null
  /**
   * What this subject is to the operation — seller, tenant, guarantor.
   *
   * `null` for subjects added before the matrix existed, and for the ones
   * nobody classified. Without it the checklist has no block to read, and the
   * old catalogue answers instead.
   */
  role: SubjectRole | null
  /**
   * Which expansion brought this subject in, when one did.
   *
   * What the per-case ceiling of expansions counts: twelve partners brought
   * in two calls of six are twelve, not six.
   */
  expandedFrom?: SubjectExpansion | null
  name: string
  /**
   * The company's previous corporate names — PJ-08 asks for the distribution
   * "incluindo razões sociais anteriores": a lawsuit filed under the old name
   * is still the company's. Empty for people and properties.
   */
  formerNames?: string[]
  document: string
  /** For a property: the numbers other bodies file it under. Empty for people and companies. */
  identifiers?: PropertyIdentifiers
  createdAt: Date
  certificates: Certificate[]
} & PersonalData &
  RegistrationData

/**
 * The home a person was given, as the routing reads it: the state as its
 * two-letter code, the city trimmed. A state no court answers to is refused
 * rather than stored — it would send every state line nowhere.
 */
export const homeOf = (personal: Partial<PersonalData>): Result<Partial<PersonalData>, AppError> => {
  if (personal.homeState === undefined && personal.homeCity === undefined) return ok(personal)

  const homeState = (personal.homeState ?? '').trim().toUpperCase()

  if (homeState && !STATES.has(homeState)) return err(invalidHomeState(homeState))

  return ok({
    ...personal,
    ...(personal.homeState !== undefined ? { homeState } : {}),
    ...(personal.homeCity !== undefined ? { homeCity: personal.homeCity.trim() } : {})
  })
}

export const emptyPersonalData = (): PersonalData => ({
  nationalId: '',
  birthDate: '',
  motherName: '',
  fatherName: '',
  gender: '',
  nationality: '',
  birthCountry: '',
  birthState: '',
  birthCity: '',
  councilState: '',
  homeState: '',
  homeCity: ''
})

export class Subject {
  private constructor(private props: SubjectProps) {
    this.adopt(props.certificates)
  }

  /**
   * A subject as stored — with its mirrors brought up to date.
   *
   * Settled on the way in because not every answer passes through this
   * aggregate: the worker writes a typed result straight into the row. Loaded
   * once, the mirror says what its source says, whoever wrote the source.
   */
  static restore(props: SubjectProps): Subject {
    const subject = new Subject(props)

    subject.settleMirrors()

    return subject
  }

  /**
   * Opens a subject already carrying the certificates its type requires — the
   * two are the same act: there is no subject without a checklist.
   */
  static open(input: {
    id: string
    diligenceId: string
    type: SubjectType
    name: string
    role?: SubjectRole | null
    profile?: CompanyProfile | null
    /** The expansion that brought this one in, when one did. */
    expandedFrom?: SubjectExpansion | null
    document: string
    personal?: Partial<PersonalData>
    registration?: Partial<RegistrationData>
    /** For a property: its IPTU registration, NIRF, CAR, RIP — whichever are known. */
    identifiers?: PropertyIdentifiers
    createdAt: Date
    nextCertificateId: () => string
    /**
     * When a certificate no robot fetches has to be in hand.
     *
     * Arrives as a function for the same reason `nextCertificateId` does: the
     * deadline comes from the catalogue's typical lead time, and the domain
     * has no business reaching for the catalogue — or for a clock — to get it.
     */
    manualDueDateFor?: (template: CertificateTemplate) => Date | null
    /**
     * The checklist the matrix composed for this subject.
     *
     * Arrives ready because composing it means reading the catalogue and the
     * pairing table, and the aggregate has no business reaching for either.
     * Absent, the old per-type catalogue answers — which is what a case with
     * no declared purpose gets.
     */
    checklist?: CertificateTemplate[]
  }): Subject {
    const subject = new Subject({
      id: input.id,
      diligenceId: input.diligenceId,
      type: input.type,
      role: input.role ?? null,
      profile: input.profile ?? null,
      expandedFrom: input.expandedFrom ?? null,
      name: input.name,
      document: input.document,
      ...emptyPersonalData(),
      ...input.personal,
      ...emptyRegistrationData(),
      ...input.registration,
      identifiers: input.type === 'property' ? cleanIdentifiers(input.identifiers ?? {}) : {},
      createdAt: input.createdAt,
      certificates: []
    })

    subject.withCertificates(
      (input.checklist ?? CERTIFICATE_CATALOGUE[input.type]).map(template =>
        Certificate.open({
          id: input.nextCertificateId(),
          subjectId: input.id,
          template,
          // A certificate no robot fetches joins the manual queue the moment
          // the subject exists. A queue somebody has to remember to fill is a
          // queue that runs empty while the work piles up.
          manualDueAt: template.automated ? null : (input.manualDueDateFor?.(template) ?? null),
          missingFields: subject.missingFieldsFor(template),
          createdAt: input.createdAt
        })
      )
    )

    return subject
  }

  get id() {
    return this.props.id
  }

  get diligenceId() {
    return this.props.diligenceId
  }

  get type() {
    return this.props.type
  }

  get name() {
    return this.props.name
  }

  get document() {
    return this.props.document
  }

  get role() {
    return this.props.role
  }

  get expandedFrom(): SubjectExpansion | null {
    return this.props.expandedFrom ?? null
  }

  get profile() {
    return this.props.profile
  }

  /** Records what the registry said, replacing whatever it said before. */
  describeCompany(profile: CompanyProfile): void {
    this.props = { ...this.props, profile }
  }

  /** The company's previous corporate names, each once. */
  get formerNames(): string[] {
    return this.props.formerNames ?? []
  }

  /**
   * Records the names the company had before.
   *
   * Replaces the list rather than adding to it, so a name typed wrong can be
   * taken out. Blanks and repeats are dropped, and the current name is not a
   * former one. Only a company has them: a person's previous name is another
   * question (marriage, the civil registry), not this one.
   */
  recordFormerNames(names: readonly string[]): Result<void, AppError> {
    if (this.props.type !== 'cnpj') {
      return err(invalidInput('Razões sociais anteriores só existem para empresa (CNPJ).'))
    }

    const current = this.props.name.trim().toLowerCase()
    const seen = new Set<string>()
    const formerNames = names
      .map(name => name.trim().replace(/\s+/g, ' '))
      .filter(name => {
        const key = name.toLowerCase()

        if (!name || key === current || seen.has(key)) return false

        seen.add(key)

        return true
      })

    this.props = { ...this.props, formerNames }

    return ok()
  }

  /** Every name a search for this company has to be run under — the current one first. */
  searchNames(): string[] {
    return [this.props.name, ...this.formerNames]
  }

  /** Where this property is registered — empty for people and companies. */
  get registration(): RegistrationData {
    return {
      registryOffice: this.props.registryOffice,
      city: this.props.city,
      state: this.props.state
    }
  }

  /** The numbers other bodies file this property under — IPTU, NIRF, CAR, RIP. */
  get identifiers(): PropertyIdentifiers {
    return { ...(this.props.identifiers ?? {}) }
  }

  /**
   * Records the numbers a property is filed under elsewhere.
   *
   * Merged over what is there, and a blank clears its field: the form sends
   * what the person sees, and a number erased on the screen is gone.
   */
  recordIdentifiers(patch: Partial<Record<keyof PropertyIdentifiers, string>>): void {
    if (this.props.type !== 'property') return

    this.props = { ...this.props, identifiers: cleanIdentifiers({ ...this.props.identifiers, ...patch }) }
  }

  get certificates(): readonly Certificate[] {
    return this.props.certificates
  }

  get personal(): PersonalData {
    const {
      nationalId,
      birthDate,
      motherName,
      fatherName,
      gender,
      nationality,
      birthCountry,
      birthState,
      birthCity,
      councilState,
      homeState,
      homeCity
    } = this.props

    return {
      nationalId,
      birthDate,
      motherName,
      fatherName,
      gender,
      nationality,
      birthCountry,
      birthState,
      birthCity,
      councilState,
      homeState,
      homeCity
    }
  }

  get createdAt() {
    return this.props.createdAt
  }

  isIndividual(): boolean {
    return this.props.type === 'cpf'
  }

  certificateById(id: string): Certificate | null {
    return this.props.certificates.find(certificate => certificate.id === id) ?? null
  }

  /** Which of a template's required fields this subject still does not have. */
  missingFieldsFor(template: Pick<CertificateTemplate, 'requires'>): PersonalField[] {
    if (!this.isIndividual()) return []

    return (template.requires ?? []).filter(field => !String(this.props[field] ?? '').trim())
  }

  /**
   * Updates the record and recomputes, on every certificate, what is still
   * missing — which is what unblocks a search stuck on "missing data".
   */
  /**
   * Fills from the registry what the record still lacks — never over what a
   * person typed — and stamps that the registry was asked, so it is paid for
   * once. The fields it filled, which is also what reopens the lines that
   * waited for them.
   */
  fillFromRegistry(found: RegisteredPerson | null, at: Date): (keyof RegisteredPerson)[] {
    const filled = (Object.entries(registeredPersonOf(found)) as [keyof RegisteredPerson, string][]).filter(
      ([field, value]) => Boolean(value?.trim()) && !String(this.props[field] ?? '').trim()
    )

    if (filled.length) this.update(Object.fromEntries(filled))

    this.markRegistryAsked(at)

    return filled.map(([field]) => field)
  }

  /** Stamps every line: the registry answered about this person, whatever it said. */
  markRegistryAsked(at: Date): void {
    for (const certificate of this.props.certificates) certificate.stampRegistryAsked(at)
  }

  /** Whether a line still out waits for a field of this person. */
  waitsForPersonalData(): boolean {
    return this.props.certificates.some(certificate => !certificate.isResolved() && certificate.missingFields.length > 0)
  }

  /**
   * A person the registry may still tell something: registered by a full CPF,
   * missing a field it knows, and never asked about — the registry is a charge.
   */
  wantsRegistry(): boolean {
    if (this.props.type !== 'cpf' || this.awaitsDocument()) return false
    if (this.props.certificates.some(certificate => certificate.registryAsked())) return false

    return REGISTRY_FIELDS.some(field => !String(this.props[field] ?? '').trim())
  }

  update(patch: Partial<{ name: string; document: string } & PersonalData>): void {
    this.props = { ...this.props, ...patch }

    for (const certificate of this.props.certificates) {
      const template = CERTIFICATE_CATALOGUE[this.props.type].find(item => item.name === certificate.name)

      certificate.setMissingFields(template ? this.missingFieldsFor(template) : [])
      certificate.reopenForNewData()
    }
  }

  /**
   * Opens the documents a condition just summoned.
   *
   * The second wave of collection lands here: the matrícula was read, a trigger
   * fired, and what it demands joins a subject that already exists. Anything
   * already on the list is skipped by name — a case re-read twice must not end
   * up paying twice for the same search.
   */
  addCertificates(input: {
    templates: CertificateTemplate[]
    summonedBy: string
    nextCertificateId: () => string
    manualDueDateFor?: (template: CertificateTemplate) => Date | null
    at: Date
  }): Certificate[] {
    const opened = input.templates
      .filter(template => !this.certificateByName(template.name))
      .map(template =>
        Certificate.open({
          id: input.nextCertificateId(),
          subjectId: this.props.id,
          template,
          manualDueAt: template.automated ? null : (input.manualDueDateFor?.(template) ?? null),
          missingFields: this.missingFieldsFor(template),
          summonedBy: input.summonedBy,
          createdAt: input.at
        })
      )

    if (opened.length) this.withCertificates([...this.props.certificates, ...opened])

    return opened
  }

  /**
   * A person's record of a finding no catalogue item reveals.
   *
   * Stored as a certificate of its own because that is what it is: a reading
   * of a document — the matrícula-mãe, the vistoria report — by someone who
   * answers for it. It gets the same treatment as any other: it shows in the
   * case, the evidence is its text, a file can be attached to it, and it goes
   * into the opinion. `found: false` is the check done and nothing found,
   * which is an answer too.
   *
   * Recorded as read and confirmed: the person who wrote it is the one who
   * looked, and a homónimo warning on it would be noise.
   */
  recordManualFinding(input: {
    id: string
    ruleId: string
    ruleTitle: string
    found: boolean
    evidence: string
    recordedBy: string
    at: Date
  }): Result<Certificate, AppError> {
    const evidence = input.evidence.trim()

    if (evidence.length < MIN_EVIDENCE_LENGTH) {
      return err(
        forbidden(
          `Descreva o que foi visto e onde (ao menos ${MIN_EVIDENCE_LENGTH} caracteres). Sem a evidência, o registro não se distingue de um palpite.`
        )
      )
    }

    const certificate = Certificate.restore({
      id: input.id,
      subjectId: this.props.id,
      name: `Registro manual — [${input.ruleId}] ${input.ruleTitle}`,
      category: 'technical',
      status: input.found ? 'adverse' : 'clear',
      automated: false,
      searchCostCents: 0,
      summary: evidence.split('\n')[0].slice(0, 200),
      details: evidence,
      origin: 'manual',
      analyzedAt: input.at,
      analyzedByAi: false,
      subjectConfirmed: true,
      trackingMetadata: { [MANUAL_FINDING_KEY]: input.ruleId, recordedBy: input.recordedBy },
      createdAt: input.at
    })

    this.withCertificates([...this.props.certificates, certificate])

    return ok(certificate)
  }

  /**
   * Opens a fresh line for a certificate whose validity ran out.
   *
   * A new certificate and not the same one reset, deliberately: the expired
   * document and what it said are what somebody took a decision on, and
   * overwriting them would erase the very thing the alert has to compare the
   * new answer against.
   *
   * `null` when no robot can fetch it — a manual certificate cannot be
   * reissued by a routine, and pretending otherwise would leave the case
   * looking refreshed.
   */
  reissue(
    certificate: Certificate,
    input: { nextCertificateId: () => string; at: Date; reason: string }
  ): Certificate | null {
    // A mirror is never fetched: renewing it is renewing its source, whose
    // answer comes back to it on arrival. A line opened for the mirror itself
    // would be refused by the queue and wait forever.
    if (this.isMirror(certificate)) return this.reissueSourceOf(certificate, input)

    const template = templateFor(this.props.type, certificate.name)

    if (!template || !template.automated) return null

    const opened = Certificate.open({
      id: input.nextCertificateId(),
      subjectId: this.props.id,
      template,
      manualDueAt: null,
      missingFields: this.missingFieldsFor(template),
      summonedBy: input.reason,
      createdAt: input.at
    })

    this.withCertificates([...this.props.certificates, opened])

    return opened
  }

  /** The source's line already on its way, or a fresh one for it. */
  private reissueSourceOf(
    mirror: Certificate,
    input: { nextCertificateId: () => string; at: Date; reason: string }
  ): Certificate | null {
    const mirrorOf = templateFor(this.props.type, mirror.name)?.mirrorOf
    const newest = mirrorOf ? this.newestFirst(mirrorOf)[0] : undefined

    if (!newest) return null
    if (!newest.isResolved()) return newest

    return this.reissue(newest, input)
  }

  /** Opens a manual re-check of a document and puts it on the queue. */
  recheck(certificate: Certificate, input: { nextCertificateId: () => string; at: Date; reason: string; dueAt: Date }): Certificate {
    const opened = Certificate.recheckOf(certificate, {
      id: input.nextCertificateId(),
      at: input.at,
      reason: input.reason,
      dueAt: input.dueAt
    })

    this.withCertificates([...this.props.certificates, opened])

    return opened
  }

  /**
   * The newest line of each document — what the case says today.
   *
   * A reissue leaves the old line in place on purpose, and anything that asks
   * "is this expired" of every line would find the old one forever and pay for
   * it again on every sweep.
   */
  currentLines(): Certificate[] {
    const newest = new Map<string, Certificate>()

    for (const certificate of this.props.certificates) {
      const seen = newest.get(certificate.name)

      if (!seen || certificate.createdAt.getTime() >= seen.createdAt.getTime()) newest.set(certificate.name, certificate)
    }

    return [...newest.values()]
  }

  /**
   * What an earlier copy of this same document said, if one did.
   *
   * The comparison a monitoring alert rests on: a certificate that comes back
   * adverse on its own is the diligence working, and one that comes back
   * adverse where a previous copy came back clean is news.
   */
  priorAnswerFor(certificate: Certificate): Certificate | null {
    return (
      this.props.certificates
        .filter(other => other.id !== certificate.id && other.name === certificate.name && other.isResolved())
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0] ?? null
    )
  }

  /**
   * The certificates worth sending to the portal.
   *
   * A mirror is out even looking queueable on its own: only the subject knows
   * that another certificate on this same list already asks that question.
   */
  queueableCertificates(at?: Date, options: { anticipate?: boolean } = {}): Certificate[] {
    if (this.awaitsDocument()) return []

    return this.props.certificates.filter(certificate => certificate.isQueueable(at, options) && !this.isMirror(certificate))
  }

  /**
   * A person or company still without the document every source is asked by.
   *
   * A partner brought in from the registry arrives like this — the public
   * record masks the CPF. Searching anyway sent an empty CPF to every
   * provider, each refused it, and the sweep sent it again every five minutes.
   * A masked one (`***366955**`) is no document either: it is not empty, and
   * it still asks for a CPF nobody has.
   */
  awaitsDocument(): boolean {
    if (this.props.type === 'property') return false

    const digits = this.props.document.replace(/\D/g, '').length

    return digits !== (this.props.type === 'cpf' ? 11 : 14)
  }

  /**
   * Whether this certificate's answer comes from another one on the list.
   *
   * Only when that other one is on the list: a mirror whose source the
   * subject does not carry — opened before the checklist brought sources
   * along (DIL-126) — has nobody to copy from, and is fetched as itself.
   */
  isMirror(certificate: Certificate): boolean {
    const mirrorOf = templateFor(this.props.type, certificate.name)?.mirrorOf

    return Boolean(mirrorOf && this.certificateByName(mirrorOf))
  }

  /**
   * The certificate this one copies, when it copies one.
   *
   * The newest line that answered, when the source was reissued: the copy
   * follows what the source says today, not the first paper it ever got.
   */
  sourceOfMirror(certificate: Certificate): Certificate | null {
    const mirrorOf = templateFor(this.props.type, certificate.name)?.mirrorOf

    if (!mirrorOf) return null

    const lines = this.newestFirst(mirrorOf)

    return lines.find(other => other.isResolved()) ?? lines[0] ?? null
  }

  /**
   * The lines of a document, newest first. A line opened in the same instant
   * as another is newer when it was appended later — the list is the order
   * they were opened in.
   */
  private newestFirst(name: string): Certificate[] {
    return this.props.certificates
      .filter(other => other.name === name)
      .reverse()
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  }

  /** The certificates waiting on this one's answer. */
  mirrorsOf(certificate: Certificate): Certificate[] {
    return this.props.certificates.filter(
      other => templateFor(this.props.type, other.name)?.mirrorOf === certificate.name
    )
  }

  /**
   * Copies to every mirror what its source answered. How many it copied.
   *
   * The only place a mirror gets its answer: every certificate of the subject
   * reports here when answered (`Certificate.listenForAnswers`), and a subject
   * loaded from storage settles on the way in. A mirror that got an answer of
   * its own — somebody brought that paper too — keeps it; a disagreement
   * between the two is `divergenceOf`'s to name, not this to overwrite.
   */
  settleMirrors(): number {
    let copied = 0

    for (const mirror of this.props.certificates) {
      const source = this.sourceOfMirror(mirror)

      if (!source) continue

      if (!source.isResolved()) {
        mirror.forgetCopy()
        continue
      }

      if (mirror.matchesAnswerOf(source)) continue
      if (mirror.isResolved() && mirror.origin !== 'mirror') continue

      mirror.copyAnswerFrom(source, source.analyzedAt ?? source.createdAt)
      copied += 1
    }

    return copied
  }

  /** Replaces the list, wiring each line to report its answers here. */
  private withCertificates(certificates: Certificate[]): void {
    this.props = { ...this.props, certificates }
    this.adopt(certificates)
  }

  private adopt(certificates: readonly Certificate[]): void {
    for (const certificate of certificates) {
      certificate.listenForAnswers(() => this.settleMirrors())
      certificate.followSource(() => this.sourceOfMirror(certificate))
    }
  }

  /**
   * Why this certificate's reading is not settled, when it is not — the rule
   * is `Divergence` in `catalog/standing.ts`.
   *
   * Asked of the subject because only the subject knows which other line
   * answers the very same question.
   */
  divergenceOf(certificate: Certificate): Divergence | null {
    if (!certificate.isResolved()) return null
    if (certificate.hasUnconfirmedLink()) return 'unconfirmedSubject'

    const counterparts = [this.sourceOfMirror(certificate), ...this.mirrorsOf(certificate)].filter(
      (other): other is Certificate => Boolean(other && other.id !== certificate.id && other.isResolved())
    )

    return counterparts.some(other => other.status !== certificate.status) ? 'conflictingReading' : null
  }

  certificateByName(name: string): Certificate | null {
    return this.props.certificates.find(certificate => certificate.name === name) ?? null
  }

  anonymize(): void {
    this.props = {
      ...this.props,
      name: '(expurgado)',
      document: '',
      ...emptyPersonalData()
    }

    for (const certificate of this.props.certificates) certificate.anonymize()
  }

  toJSON() {
    return { ...this.props, certificates: this.props.certificates.map(certificate => certificate.toJSON()) }
  }
}

// ─── Diligence ──────────────────────────────────────────────────────────────

/** How long a closed diligence is kept — the case record's window in the retention policy. */
export const RETENTION_DAYS = RETENTION_POLICY.caseRecord.days ?? 365 * 5

/**
 * Who is buying, for the purposes of the matrix.
 *
 * A regulated fund is held to a stricter standard than anyone else: four rows
 * of the purchase matrix are "Essencial (fundos) / Recomendada", and the
 * compliance block is not optional for them. It is the fourth axis of the
 * requirement — after purpose, block and item.
 */
export type ClientProfile = 'standard' | 'fund'

export const CLIENT_PROFILE_LABEL: Record<ClientProfile, string> = {
  standard: 'Cliente comum',
  fund: 'Fundo ou instituição regulada'
}

/**
 * The profile a caller named, if the matrix knows it.
 *
 * Checked here and not only at the edge: an untyped caller, a script or a
 * form passing its input through reaches the case without going through Zod.
 */
export const clientProfileFrom = (value: string): Result<ClientProfile, AppError> =>
  Object.keys(CLIENT_PROFILE_LABEL).includes(value) ? ok(value as ClientProfile) : err(invalidClientProfile(value))

export type DiligenceProps = {
  id: string
  projectName: string
  address: string
  /**
   * What the diligence is for — the field that decides everything downstream.
   *
   * It is `null` only for cases opened before the matrix existed: the column
   * cannot be backfilled by guessing, and a case whose purpose nobody declared
   * has no checklist to be measured against.
   */
  purpose: Purpose | null
  clientProfile: ClientProfile
  /** The article of the LGPD the queries of this case stand on. */
  legalBasis: LegalBasis
  /** What that basis is for, in a sentence a titular would understand. */
  legalPurpose: string
  /** The operation as the team labels it: SCP, Aquisição, Leilão, Permuta. */
  kind: string
  status: DiligenceStatus
  notes: string
  ownerId: string | null
  expiresAt: Date | null
  purgedAt: Date | null
  purgeHoldReason: string | null
  /**
   * When the case left `inProgress` — stamped by the transition itself, so an
   * edit made after closing does not restart any retention clock. Null while
   * it runs, and cleared again on reopening.
   */
  closedAt?: Date | null
  /**
   * The day the deal is expected to be signed (YYYY-MM-DD), when somebody said.
   *
   * Validity is measured against it before the closing: a certificate valid
   * today and expired that day is the one the counter refuses.
   */
  expectedClosingOn?: string | null
  /** The ceiling of what the case may spend, in centavos — null when nobody capped it. */
  budgetCents?: number | null
  /** Somebody raised the ceiling: who, when, why and to how much. */
  budgetApproval?: BudgetApproval | null
  /** When the third-party pass ran on this closing, so it does not run again. */
  thirdPartyPurgedAt?: Date | null
  /** When the certificate files of this closing were discarded. */
  filesPurgedAt?: Date | null
  /**
   * The fingerprint of every dossier handed to the client before any purge,
   * oldest first. The PDF itself is not kept — keeping it would keep
   * everything the purge removed — but each copy the client holds stays
   * verifiable, and a reissue can name the one it stands in for.
   */
  issuedDossiers?: IssuedDossier[]
  /**
   * How many people the expansions ever brought into the case — removed ones
   * included. The per-case ceiling counts this, so removing a person does not
   * hand the slot back and remove + re-add cannot walk past it.
   */
  expansionsMade?: number
  createdAt: Date
  updatedAt: Date
  subjects: Subject[]
}

export type IssuedDossier = { hash: string; at: Date }

/** The fields the generic update may touch — the profile is not one of them. */
const UPDATABLE_FIELDS = [
  'projectName',
  'address',
  'purpose',
  'kind',
  'status',
  'notes',
  'legalBasis',
  'legalPurpose',
  'expectedClosingOn'
] as const

export type DiligencePatch = Partial<Pick<DiligenceProps, (typeof UPDATABLE_FIELDS)[number]>>

const isClosedStatus = (status: DiligenceStatus): boolean => status !== 'inProgress' && status !== 'purged'

export class Diligence {
  private constructor(private props: DiligenceProps) {}

  static restore(props: DiligenceProps): Diligence {
    return new Diligence(props)
  }

  static open(input: {
    id: string
    projectName: string
    address: string
    purpose: Purpose
    clientProfile?: ClientProfile
    legalBasis?: LegalBasis
    legalPurpose?: string
    kind: string
    notes?: string
    ownerId: string
    at: Date
  }): Diligence {
    return new Diligence({
      id: input.id,
      projectName: input.projectName,
      address: input.address,
      purpose: input.purpose,
      clientProfile: input.clientProfile ?? 'standard',
      // Derived from what the case is for, so the field is never blank — a
      // blank one gets filled with whatever is first in the list.
      legalBasis: input.legalBasis ?? defaultBasisFor(input.purpose ?? null),
      legalPurpose: input.legalPurpose?.trim() ?? '',
      kind: input.kind,
      status: 'inProgress',
      notes: input.notes ?? '',
      ownerId: input.ownerId,
      // Until it closes, the case record ages from its opening; closing moves
      // the date to five years from then.
      expiresAt: new Date(input.at.getTime() + RETENTION_DAYS * 24 * 60 * 60 * 1000),
      purgedAt: null,
      purgeHoldReason: null,
      createdAt: input.at,
      updatedAt: input.at,
      subjects: []
    })
  }

  get id() {
    return this.props.id
  }

  get projectName() {
    return this.props.projectName
  }

  get address() {
    return this.props.address
  }

  get purpose() {
    return this.props.purpose
  }

  get legalGround(): LegalGround {
    return { basis: this.props.legalBasis, purpose: this.props.legalPurpose }
  }

  get clientProfile() {
    return this.props.clientProfile
  }

  get kind() {
    return this.props.kind
  }

  /**
   * Whether the matrix can be applied to this case at all.
   *
   * A case without a purpose is not broken — it is older than the feature — but
   * no checklist, cost estimate or opinion template can be derived for it.
   */
  hasPurpose(): boolean {
    return this.props.purpose !== null
  }

  get status() {
    return this.props.status
  }

  get notes() {
    return this.props.notes
  }

  get ownerId() {
    return this.props.ownerId
  }

  get expiresAt() {
    return this.props.expiresAt
  }

  get purgedAt() {
    return this.props.purgedAt
  }

  get purgeHoldReason() {
    return this.props.purgeHoldReason
  }

  get createdAt() {
    return this.props.createdAt
  }

  get updatedAt() {
    return this.props.updatedAt
  }

  get expectedClosingOn(): string | null {
    return this.props.expectedClosingOn ?? null
  }

  /** The expected closing as a moment — noon UTC keeps the calendar day in every zone. */
  expectedClosingDate(): Date | null {
    const on = this.props.expectedClosingOn

    return on ? new Date(`${on}T12:00:00.000Z`) : null
  }

  get budgetCents(): number | null {
    return this.props.budgetCents ?? null
  }

  get budgetApproval(): BudgetApproval | null {
    return this.props.budgetApproval ?? null
  }

  /** What the case's queries spent, in centavos. */
  spentCents(): number {
    return spentCentsOf(this.certificates)
  }

  /**
   * Sets the ceiling. A new ceiling is a new decision, so an approval given
   * over the old one does not carry over.
   */
  setBudget(cents: number | null, at: Date): Result<void, AppError> {
    const valid = validBudget(cents)

    if (!valid.ok) return valid

    this.props = { ...this.props, budgetCents: valid.value, budgetApproval: null, updatedAt: at }

    return ok()
  }

  /**
   * Somebody with the authority raises the ceiling, with a reason.
   *
   * The reason is demanded and stays on the case: "o cliente aprovou por
   * e-mail em 12/10" is what answers, a month later, why this case cost twice
   * what it was sold for.
   */
  approveOverBudget(input: {
    byId: string
    byEmail: string
    reason: string
    upToCents: number
    at: Date
  }): Result<BudgetApproval, AppError> {
    const budget = this.props.budgetCents ?? null

    if (budget === null) return err(forbidden('Este caso não tem teto — não há o que autorizar acima dele.'))

    if (input.reason.trim().length < MIN_APPROVAL_REASON) {
      return err(forbidden('Diga por que o gasto acima do teto foi autorizado.'))
    }

    if (!Number.isInteger(input.upToCents) || input.upToCents <= budget) {
      return err(forbidden('A autorização precisa levar o teto acima do valor atual.'))
    }

    const approval: BudgetApproval = {
      byId: input.byId,
      byEmail: input.byEmail,
      at: input.at,
      reason: input.reason.trim(),
      upToCents: input.upToCents
    }

    this.props = { ...this.props, budgetApproval: approval, updatedAt: input.at }

    return ok(approval)
  }

  /** Whether spending this much more keeps the case inside its ceiling. */
  allowsSpend(additionalCents: number): Result<void, AppError> {
    return checkSpend({
      budgetCents: this.budgetCents,
      approval: this.budgetApproval,
      spentCents: this.spentCents(),
      additionalCents
    })
  }

  get thirdPartyPurgedAt(): Date | null {
    return this.props.thirdPartyPurgedAt ?? null
  }

  get filesPurgedAt(): Date | null {
    return this.props.filesPurgedAt ?? null
  }

  /** Every copy handed to the client, oldest first. */
  get issuedDossiers(): readonly IssuedDossier[] {
    return this.props.issuedDossiers ?? []
  }

  /** The last copy handed to the client — what a reissue stands in for. */
  get issuedDossier(): IssuedDossier | null {
    return this.issuedDossiers.at(-1) ?? null
  }

  get subjects(): readonly Subject[] {
    return this.props.subjects
  }

  get certificates(): readonly Certificate[] {
    return this.props.subjects.flatMap(subject => subject.certificates)
  }

  /**
   * An administrator sees every diligence; everyone else, their own.
   *
   * A diligence with no owner (imported before ownership existed) is visible
   * to administrators only — it must not leak to another user by accident.
   */
  isVisibleTo(user: { id: string; role: UserRole }): boolean {
    return user.role === 'admin' || this.props.ownerId === user.id
  }

  /**
   * Whether this person can be made responsible for a line of this case.
   *
   * Three things, all of them about the person and the case: the account is
   * active, the role may change a case (a viewer or auditor only reads), and
   * the case is one they can open — naming somebody who cannot see the work
   * would put a name on the list and leave the trip with nobody.
   */
  acceptsAssignee(person: { id: string; role: UserRole; active: boolean }): Result<void, AppError> {
    if (!person.active) return err(assigneeNotEligible('a conta está desativada.'))
    if (person.role !== 'admin' && person.role !== 'analyst') {
      return err(assigneeNotEligible('o papel dela só permite leitura.'))
    }

    if (!this.isVisibleTo(person)) return err(assigneeNotEligible('ela não tem acesso a esta diligência.'))

    return ok()
  }

  isPurged(): boolean {
    return this.props.status === 'purged'
  }

  /**
   * Whether a search is still worth paying for here.
   *
   * A diligence already approved or rejected has had its opinion written:
   * going to the portal for it would buy a document for a closed case. Only
   * the automatic routine asks — the button still obeys whoever clicked it.
   */
  isOpenForSearch(): boolean {
    return this.props.status === 'inProgress'
  }

  subjectById(id: string): Subject | null {
    return this.props.subjects.find(subject => subject.id === id) ?? null
  }

  addSubject(subject: Subject): void {
    const expansionsMade = this.expansionsMade + (subject.expandedFrom ? 1 : 0)

    this.props = { ...this.props, subjects: [...this.props.subjects, subject], expansionsMade }
  }

  /**
   * People brought in by an expansion, ever — the ones still here and the ones
   * removed since. Never less than those still on the case, for a record kept
   * before the counter existed.
   */
  get expansionsMade(): number {
    const present = this.props.subjects.filter(subject => subject.expandedFrom !== null).length

    return Math.max(this.props.expansionsMade ?? 0, present)
  }

  removeSubject(id: string): void {
    this.props = { ...this.props, subjects: this.props.subjects.filter(subject => subject.id !== id) }
  }

  /**
   * The plain fields of the case.
   *
   * Not the client's profile: that is an axis of the matrix and changes
   * through `changeClientProfile`, whose caller measures what it raised. Only
   * the listed fields are copied, so a patch carrying anything else — an
   * untyped caller, a router passing its input through — cannot slip a
   * profile change past that measurement.
   */
  update(patch: DiligencePatch, at: Date): void {
    const allowed = Object.fromEntries(
      Object.entries(patch).filter(([key]) => (UPDATABLE_FIELDS as readonly string[]).includes(key))
    ) as DiligencePatch
    const status = allowed.status ?? this.props.status

    this.props = { ...this.props, ...allowed, ...this.closingTransition(status, at), updatedAt: at }
  }

  /** Who the client is — the caller measures the checklist before and after (`profileShiftOf`). */
  changeClientProfile(value: string, at: Date): Result<void, AppError> {
    const clientProfile = clientProfileFrom(value)

    if (!clientProfile.ok) return clientProfile

    this.props = { ...this.props, clientProfile: clientProfile.value, updatedAt: at }

    return ok()
  }

  /**
   * What changing status does to the retention clocks.
   *
   * Closing stamps the moment, and every window counts from it: the case
   * record from then on, and the purge markers reset because a new closing is
   * a new window. Going from one decision to another (approved → rejected) is
   * not a new closing. Reopening clears the stamp and leaves the rest — the
   * passes that already ran on an open case are not undone.
   */
  private closingTransition(status: DiligenceStatus, at: Date): Partial<DiligenceProps> {
    const wasClosed = Boolean(this.props.closedAt)

    if (status === 'inProgress') return wasClosed ? { closedAt: null } : {}
    if (!isClosedStatus(status) || wasClosed) return {}

    return {
      closedAt: at,
      thirdPartyPurgedAt: null,
      filesPurgedAt: null,
      expiresAt: dueAtFor({ dataClass: 'caseRecord', closedAt: at, openedAt: this.props.createdAt })
    }
  }

  /** Freezes the purge — a court order, an ongoing investigation. */
  holdPurge(reason: string, at: Date): void {
    this.props = { ...this.props, purgeHoldReason: reason.slice(0, 1000), updatedAt: at }
  }

  releasePurge(at: Date): void {
    this.props = { ...this.props, purgeHoldReason: null, updatedAt: at }
  }

  /**
   * Whether the case is closed, and when it was.
   *
   * Every retention window counts from here — the data was needed while the
   * deal was live. `null` while it is still running — and a running case is
   * never purged mid-diligence: it only ages out once it counts as abandoned
   * (`ABANDONED_CASE_DAYS`), so "deixa em andamento" still does not keep data
   * forever.
   */
  get closedAt(): Date | null {
    return this.props.closedAt ?? null
  }

  /** Whether any retention pass already took something out of this case. */
  hasBeenPartiallyPurged(): boolean {
    return Boolean(this.props.thirdPartyPurgedAt || this.props.filesPurgedAt)
  }

  /** The third-party pass ran: its data is gone, the case carries on. */
  markThirdPartyPurged(at: Date): void {
    this.props = { ...this.props, thirdPartyPurgedAt: at, updatedAt: at }
  }

  /**
   * The certificate files are discarded; the fingerprint of each stays.
   *
   * After two years what proves what was looked at is the dossier and the
   * hashes it carries — the raw documents have outlived their reason.
   */
  discardCertificateFiles(at: Date): string[] {
    // A mirror points at its sibling's file: one document, listed once.
    const paths = [...new Set(this.certificates.flatMap(certificate => (certificate.filePath ? [certificate.filePath] : [])))]

    for (const certificate of this.certificates) certificate.discardFile()

    this.props = { ...this.props, filesPurgedAt: at, updatedAt: at }

    return paths
  }

  /**
   * Whether a dossier handed out now is still a copy of the original.
   *
   * Only before any purge: after one, the PDF no longer is the document the
   * client received, and recording it would let a reissue pass as the
   * original.
   */
  canRecordIssuedDossier(): boolean {
    return !this.hasBeenPartiallyPurged() && !this.isPurged()
  }

  /**
   * Remembers the fingerprint of a dossier handed to the client, beside the
   * earlier ones — never in their place: each copy may be the one somebody
   * later has to check.
   */
  recordIssuedDossier(hash: string, at: Date): boolean {
    if (!this.canRecordIssuedDossier()) return false

    this.props = { ...this.props, issuedDossiers: [...this.issuedDossiers, { hash, at }] }

    return true
  }

  canBePurged(at: Date): Result<void, AppError> {
    if (this.isPurged()) return err({ code: 'ALREADY_PURGED', message: 'Esta diligência já foi expurgada.' })
    if (this.props.purgeHoldReason) return err(purgeOnHold(this.props.purgeHoldReason))
    if (!this.props.expiresAt || this.props.expiresAt > at) {
      return err(forbidden('A diligência ainda está dentro do prazo de retenção.'))
    }

    return ok()
  }

  /**
   * Anonymization, irreversible: personal data goes, the shell stays.
   *
   * What survives is what proves the operation existed — id, dates, counts.
   * It is the difference between "we deleted the record" and "we can show a
   * regulator that we deleted it".
   */
  anonymize(at: Date): void {
    for (const subject of this.props.subjects) subject.anonymize()

    this.props = {
      ...this.props,
      projectName: '(expurgada)',
      address: '',
      notes: '',
      status: 'purged',
      purgedAt: at,
      updatedAt: at
    }
  }

  toJSON() {
    return { ...this.props, subjects: this.props.subjects.map(subject => subject.toJSON()) }
  }
}

// ─── Certificate catalogue ──────────────────────────────────────────────────
// Every subject type is born with the right set of certificates. This table is
// what `Subject.open` reads — the checklist a Brazilian real-estate diligence
// actually goes through.

export type CertificateTemplate = {
  name: string
  category: CertificateCategory
  /** Which body issues it — and, through it, how the search gets there. */
  source: SourceKey
  /** `true` when a scraper exists for it. */
  automated: boolean
  /** What a single search costs at the portal, in centavos. */
  searchCostCents?: number
  /**
   * How long the market accepts it, in days.
   *
   * Left out when the document has no shelf life: a snapshot of the day (a
   * registration status, a sanctions list) or a one-off act (a habite-se, a
   * survey). Only what expires goes into the expiry queue.
   */
  validityDays?: number
  /** Personal fields without which the portal refuses the request. */
  requires?: PersonalField[]
  /** The portal accepts the request and mails the PDF later. */
  answersByEmail?: boolean
  /**
   * Another certificate on this same list that answers the very same query.
   *
   * Two names for one trip to the portal: the CJF issues a single unified
   * certificate covering every federal court, and the TST issues one CNDT for
   * the whole country. The checklist a diligence goes through names both, so
   * both stay — but only the source is searched, and its answer is copied
   * here. Searching twice would pay twice for the same paper.
   */
  mirrorOf?: string
  /**
   * The catalogue item the line answers, for lines a trigger opens under a
   * name no template has — the certidão de objeto e pé is named after its
   * lawsuit. Stamped on the line, so what it is does not hang on its name.
   */
  itemId?: string
  /** The lawsuit the line is about, for the documents asked per lawsuit. */
  lawsuit?: string
}

/** Where a line keeps the catalogue item it was opened for, inside `trackingMetadata`. */
export const CATALOG_ITEM_KEY = 'catalogItem'
/** Where a line keeps the lawsuit it was opened for, inside `trackingMetadata`. */
export const LAWSUIT_KEY = 'lawsuit'
/** Why the line holds a proof of query instead of a certificate, inside `trackingMetadata`. */
const QUERY_RECORD_KEY = 'queryRecord'
/** Where the answer of a proof of query is checked at the source, inside `trackingMetadata`. */
const VERIFY_AT_KEY = 'verifyAt'

/** A line whose file is the proof of a query, and why no certificate came. */
export type QueryRecordStamp = { missingDocument: string; verifyAt: string | null }

const queryRecordStamps = (stamp: QueryRecordStamp): Record<string, string> => ({
  [QUERY_RECORD_KEY]: stamp.missingDocument,
  ...(stamp.verifyAt ? { [VERIFY_AT_KEY]: stamp.verifyAt } : {})
})

const withoutQueryRecord = (tracking: Record<string, string> | null): Record<string, string> | null => {
  if (!tracking) return null

  const { [QUERY_RECORD_KEY]: _record, [VERIFY_AT_KEY]: _verifyAt, ...rest } = tracking

  return Object.keys(rest).length ? rest : null
}

/** Where a line counts the sweeps that ended in a technical failure, inside `trackingMetadata`. */
const TECHNICAL_ROUNDS_KEY = 'technicalRounds'
/** When a refusal of the data was given its one more look, inside `trackingMetadata`. */
const DATA_RECHECKED_KEY = 'dataRecheckedAt'
const DATA_RECHECK_AFTER_MS = 24 * 60 * 60 * 1000

/** When the person's registry was asked about this line's subject, inside `trackingMetadata`. */
const REGISTRY_ASKED_KEY = 'registryAskedAt'

/** How many times the reading model failed on the paper this line holds, and when it last did. */
const READING_FAILURES_KEY = 'readingFailures'
const READING_FAILED_AT_KEY = 'readingFailedAt'

/** Sweeps that go back to a failed line right away (each sweep already retries its job). */
export const MAX_TECHNICAL_ROUNDS = 3

/** How long a body that promised the certificate by e-mail has before it is asked again. */
export const EMAIL_WAIT_MS = 24 * 60 * 60 * 1000

const HOUR_MS = 60 * 60 * 1000

/** How long a line that failed `rounds` times waits before the next one: an hour, six, then a day. */
const patienceAfter = (rounds: number): number => {
  if (rounds <= MAX_TECHNICAL_ROUNDS) return HOUR_MS
  if (rounds === MAX_TECHNICAL_ROUNDS + 1) return 6 * HOUR_MS

  return 24 * HOUR_MS
}

/** What a template stamps on the lines it opens, besides its name. */
const stampsOf = (template: CertificateTemplate, missingFields: PersonalField[]): Record<string, string> | null => {
  const stamps: Record<string, string> = {}

  if (missingFields.length) stamps.missingFields = missingFields.join(',')
  if (template.itemId) stamps[CATALOG_ITEM_KEY] = template.itemId
  if (template.lawsuit) stamps[LAWSUIT_KEY] = template.lawsuit

  return Object.keys(stamps).length ? stamps : null
}

const INDIVIDUAL_CERTIFICATES: CertificateTemplate[] = [
  // O robô consulta a base da Serasa, não o cartório de protestos: o nome
  // diz o que chega, para ninguém arquivar isto como certidão de cartório.
  {
    name: 'Consulta de Restrições (Serasa)',
    category: 'financial',
    source: 'serasa',
    automated: true,
    searchCostCents: 1606,
    validityDays: 90
  },
  {
    name: 'Certidão Tributária Federal (RFB/PGFN)',
    category: 'tax',
    source: 'rfb',
    automated: true,
    validityDays: 180,
    requires: ['birthDate']
  },
  { name: 'Certidão Tributária Estadual (SEFAZ)', category: 'tax', source: 'sefaz', automated: true, validityDays: 90 },
  { name: 'Certidão de Débitos Municipais', category: 'tax', source: 'cityHall', automated: true, validityDays: 90 },
  {
    name: 'Certidão Justiça Federal Cível',
    category: 'judicial',
    source: 'trf',
    automated: true,
    validityDays: 90,
    answersByEmail: true
  },
  {
    name: 'Certidão de Origem Cível TJ',
    category: 'judicial',
    source: 'tj',
    automated: true,
    validityDays: 90,
    requires: ['nationalId'],
    answersByEmail: true
  },
  {
    name: 'Certidão de Falências e Concordatas TJ',
    category: 'judicial',
    source: 'tj',
    automated: true,
    validityDays: 90,
    requires: ['nationalId'],
    answersByEmail: true
  },
  {
    name: 'Certidão de Inventário TJ',
    category: 'judicial',
    source: 'tj',
    automated: true,
    validityDays: 90,
    requires: ['nationalId'],
    answersByEmail: true
  },
  {
    name: 'Certidão de Origem Criminal TJ',
    category: 'criminal',
    source: 'tj',
    automated: true,
    validityDays: 90,
    requires: ['nationalId', 'birthDate', 'motherName', 'gender'],
    answersByEmail: true
  },
  {
    // Emitida pela Polícia Federal (e-pol SINIC), de alcance nacional — a SSP
    // é estadual e não tem automação aqui.
    name: 'Certidão de Antecedentes Criminais (Polícia Federal)',
    category: 'criminal',
    source: 'ssp',
    automated: true,
    validityDays: 90,
    requires: ['birthDate', 'motherName']
  },
  { name: 'Certidão Trabalhista (CNDT)', category: 'labor', source: 'tst', automated: true, validityDays: 180 },
  {
    name: 'Certidão Negativa de Débitos Trabalhistas (TRT 15)',
    category: 'labor',
    source: 'tst',
    automated: true,
    validityDays: 180,
    mirrorOf: 'Certidão Trabalhista (CNDT)'
  },
  // A CNDT diz se há débito executado; estas dizem se há ação em curso — que é
  // o que ainda não virou débito e nem por isso deixa de ser risco.
  { name: 'TRT 2ª Região (PJe) — Ações em Andamento', category: 'labor', source: 'trt', automated: true, validityDays: 90 },
  { name: 'TRT 15ª Região (PJe) — Ações em Andamento', category: 'labor', source: 'trt', automated: true, validityDays: 90 },
  {
    name: 'Certidão TRF 3ª Região',
    category: 'judicial',
    source: 'trf',
    automated: true,
    validityDays: 90,
    answersByEmail: true,
    mirrorOf: 'Certidão Justiça Federal Cível'
  },
  { name: 'TJSP 1º Grau — Cíveis', category: 'judicial', source: 'tj', automated: true, validityDays: 90 },
  { name: 'STF — Supremo Tribunal Federal', category: 'judicial', source: 'stf', automated: true, validityDays: 90 },
  { name: 'STJ — Superior Tribunal de Justiça', category: 'judicial', source: 'stj', automated: true, validityDays: 90 },
  { name: 'PEP — Pessoa Exposta Politicamente', category: 'compliance', source: 'cgu', automated: true }
]

const COMPANY_CERTIFICATES: CertificateTemplate[] = [
  { name: 'Situação Cadastral CNPJ (RFB)', category: 'registration', source: 'brasilApi', automated: true },
  { name: 'Quadro Societário Atualizado (QSA)', category: 'registration', source: 'brasilApi', automated: true },
  { name: 'Contrato Social / Estatuto', category: 'registration', source: 'client', automated: false },
  { name: 'CREA — Conselho Regional de Engenharia', category: 'registration', source: 'crea', automated: true, validityDays: 90 },
  { name: 'Certidão de Débitos (CND - RFB/PGFN)', category: 'tax', source: 'rfb', automated: true, validityDays: 180 },
  { name: 'Certidão Tributária Estadual (SEFAZ)', category: 'tax', source: 'sefaz', automated: true, validityDays: 90 },
  { name: 'Certidão de Débitos Municipais', category: 'tax', source: 'cityHall', automated: true, validityDays: 90 },
  // Dois modelos saíram daqui em 17/09/2026, e o motivo é o mesmo: pediam ao
  // mesmo portal a mesma certidão que outro modelo já pedia, e o caso pagava
  // duas buscas para receber o mesmo papel com outro nome.
  //
  // `Certidão INSS (CRFB)` — o previdenciário entrou na certidão conjunta
  // RFB/PGFN em 2014; a CRFB separada não é mais emitida.
  // `ECND — Estadual de Não Devedor` — é a certidão da SEFAZ com outro nome.
  //
  // Os dois continuam no de-para de `reconciliation.ts`: casos antigos têm
  // linhas com esses nomes, e sem o mapa elas perderiam item, validade e red
  // flag na tela.
  { name: 'E-CRDA — Certidão de Regularidade de DARF', category: 'tax', source: 'rfb', automated: true, validityDays: 180 },
  { name: 'Certidão Trabalhista TST', category: 'labor', source: 'tst', automated: true, validityDays: 180 },
  // `Certidão TRT Regional` saiu em 02/10/2026 (DIL-076): não existe certidão
  // com esse nome. Era a CNDT do TST copiada, e as consultas regionais de
  // verdade são as dos TRTs logo abaixo. Ver `RETIRED_TEMPLATES`.
  { name: 'TRT 2ª Região (PJe) — Ações em Andamento', category: 'labor', source: 'trt', automated: true, validityDays: 90 },
  { name: 'TRT 15ª Região (PJe) — Ações em Andamento', category: 'labor', source: 'trt', automated: true, validityDays: 90 },
  { name: 'FGTS — Caixa Econômica Federal', category: 'labor', source: 'caixa', automated: true, validityDays: 30 },
  {
    name: 'Certidão de Falências e Recuperação Judicial TJ',
    category: 'judicial',
    source: 'tj',
    automated: true,
    validityDays: 90,
    answersByEmail: true
  },
  {
    name: 'Certidão de Ações Cíveis TJ',
    category: 'judicial',
    source: 'tj',
    automated: true,
    validityDays: 90,
    answersByEmail: true
  },
  { name: 'TJSP 1º Grau — Cíveis', category: 'judicial', source: 'tj', automated: true, validityDays: 90 },
  {
    name: 'Certidão TRF 3ª Região',
    category: 'judicial',
    source: 'trf',
    automated: true,
    validityDays: 90,
    answersByEmail: true
  },
  { name: 'STF — Supremo Tribunal Federal', category: 'judicial', source: 'stf', automated: true, validityDays: 90 },
  { name: 'STJ — Superior Tribunal de Justiça', category: 'judicial', source: 'stj', automated: true, validityDays: 90 },
  { name: 'MPSP — Ministério Público SP', category: 'judicial', source: 'mp', automated: true, validityDays: 90 },
  // PJ-10 in two papers, as the two courts issue it: the state court's and the
  // federal court's criminal distribution of the company itself. One line for
  // both was asked as one Direct Data product that does not reach the TJSP —
  // every company seated in SP came back "descontinuada".
  { name: 'Certidão negativa do MPF (PJ)', category: 'judicial', source: 'mp', automated: true, validityDays: 90 },
  { name: 'Certidão de Distribuição Criminal TJ (PJ)', category: 'criminal', source: 'tj', automated: true, validityDays: 90, answersByEmail: true },
  { name: 'Certidão de Distribuição Criminal da Justiça Federal (PJ)', category: 'criminal', source: 'trf', automated: true, validityDays: 90 },
  { name: 'Banco Central — PAS', category: 'compliance', source: 'bacen', automated: true }
]

const PROPERTY_CERTIFICATES: CertificateTemplate[] = [
  {
    name: 'Matrícula Atualizada (máx. 30 dias)',
    category: 'propertyRegistry',
    source: 'onr',
    automated: true,
    searchCostCents: 5000,
    validityDays: 30
  },
  {
    name: 'Certidão de Ônus Reais',
    category: 'propertyRegistry',
    source: 'onr',
    automated: true,
    searchCostCents: 5000,
    validityDays: 30
  },
  { name: 'Certidão de Valor Venal', category: 'financial', source: 'cityHall', automated: true },
  { name: 'Certidão de IPTU', category: 'financial', source: 'cityHall', automated: true },
  {
    name: 'Certidão de Débitos Municipais do Imóvel',
    category: 'financial',
    source: 'cityHall',
    automated: true,
    validityDays: 90
  },
  {
    name: 'Certidão Negativa de Débitos Condominiais',
    category: 'financial',
    source: 'condominium',
    automated: false,
    validityDays: 30
  },
  {
    name: 'ITR — Imposto Territorial Rural (Receita Federal)',
    category: 'financial',
    source: 'rfb',
    automated: false,
    validityDays: 180
  },
  {
    name: 'IBAMA — Débitos e Embargos Ambientais',
    category: 'environmental',
    source: 'ibama',
    automated: true,
    searchCostCents: 15,
    validityDays: 90
  },
  {
    name: 'Certidão Ambiental Municipal (SVMA/Secretaria local)',
    category: 'environmental',
    source: 'cityHall',
    automated: false,
    validityDays: 90
  },
  { name: 'CAR — Cadastro Ambiental Rural', category: 'environmental', source: 'sicar', automated: true, searchCostCents: 10 },
  {
    name: 'CETESB — Companhia Ambiental do Estado de SP',
    category: 'environmental',
    source: 'cetesb',
    automated: true,
    validityDays: 90
  },
  { name: 'Planta e Memorial Descritivo', category: 'technical', source: 'client', automated: false },
  { name: 'Habite-se / Auto de Conclusão', category: 'technical', source: 'cityHall', automated: false }
]


export const CERTIFICATE_CATALOGUE: Record<SubjectType, CertificateTemplate[]> = {
  cpf: INDIVIDUAL_CERTIFICATES,
  cnpj: COMPANY_CERTIFICATES,
  property: PROPERTY_CERTIFICATES
}

/**
 * Templates no new subject receives, kept for the lines old cases already have.
 *
 * A retired name does not leave the database: a case opened before keeps the
 * line, and that line still needs its template to behave as it did — the TRT
 * Regional one copied the TST's answer, and without the template it would turn
 * into a search of its own, paid, for a certificate that does not exist.
 * Opening a subject reads `CERTIFICATE_CATALOGUE` only; `templateFor` reads
 * both.
 */
export const RETIRED_TEMPLATES: Record<SubjectType, CertificateTemplate[]> = {
  cpf: [],
  cnpj: [
    {
      name: 'Certidão TRT Regional',
      category: 'labor',
      source: 'tst',
      automated: true,
      validityDays: 90,
      mirrorOf: 'Certidão Trabalhista TST'
    }
  ],
  property: []
}

export const templateFor = (type: SubjectType, name: string): CertificateTemplate | null =>
  CERTIFICATE_CATALOGUE[type].find(template => template.name === name) ??
  RETIRED_TEMPLATES[type].find(template => template.name === name) ??
  null

/**
 * The whole catalogue in one flat list, each item carrying the type it belongs
 * to — the shape a report reads, where "17 certificates for a person" matters
 * less than "everything that comes from a city hall".
 */
export const catalogueEntries = (): { subjectType: SubjectType; template: CertificateTemplate }[] =>
  (Object.keys(CERTIFICATE_CATALOGUE) as SubjectType[]).flatMap(subjectType =>
    CERTIFICATE_CATALOGUE[subjectType].map(template => ({ subjectType, template }))
  )

/** What the plan calls this kind of subject, for the "not in your plan" message. */
export const SUBJECT_TYPE_LABEL: Record<SubjectType, string> = {
  cpf: 'pessoa física',
  cnpj: 'pessoa jurídica',
  property: 'imóvel'
}

/** Loads a diligence and checks it can be seen, in one move. */
/** What the listing carries — re-exported so the screens can name the shape. */
export type { DiligenceSummary } from './ports'

export const visibleOrNotFound = (
  diligence: Diligence | null,
  user: { id: string; role: UserRole }
): Result<Diligence, AppError> => {
  if (!diligence || !diligence.isVisibleTo(user)) return err(notFound('Diligência'))

  return ok(diligence)
}
